/**
 * Centralised query key factory for TanStack Query.
 * Every query key used in the app MUST be defined here.
 */
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
    inviteStatus: (userId: string, token: string) =>
      [...queryKeys.auth.all, 'invite-status', userId, token] as const,
  },
  organizations: {
    all: ['organizations'] as const,
    list: () => [...queryKeys.organizations.all, 'list'] as const,
    byId: (id: string) => [...queryKeys.organizations.all, id] as const,
    subsidiaries: (id: string) => [...queryKeys.organizations.all, id, 'subsidiaries'] as const,
  },
  subsidiaries: {
    all: ['subsidiaries'] as const,
    byId: (id: string) => [...queryKeys.subsidiaries.all, id] as const,
    list: () => [...queryKeys.subsidiaries.all, 'list'] as const,
    search: (params: Record<string, unknown>) =>
      [...queryKeys.subsidiaries.all, 'search', params] as const,
    health: () => [...queryKeys.subsidiaries.all, 'health'] as const,
  },
  /** Organization members */
  members: {
    all: ['members'] as const,
    list: (subsidiaryId: string, page?: number, limit?: number) =>
      ['members', 'list', subsidiaryId, page, limit] as const,
    /** Every member of a subsidiary, flattened for actor-id resolution. Keyed
     *  separately from `list` so it never collides with a paginated page. */
    directory: (subsidiaryId: string) => ['members', 'directory', subsidiaryId] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.users.all, 'list', params] as const,
    search: (params: Record<string, unknown>) =>
      [...queryKeys.users.all, 'search', params] as const,
    byId: (id: string) => [...queryKeys.users.all, id] as const,
  },
  rbac: {
    all: ['rbac'] as const,
    /** Roles */
    roles: () => [...queryKeys.rbac.all, 'roles'] as const,
    rolesList: () => ['rbac-roles', 'list'] as const,
    rolesSearch: (filters: Record<string, unknown>, page?: number, size?: number) =>
      ['rbac-roles', 'search', filters, page, size] as const,
    roleById: (id: string) => ['rbac-roles', 'detail', id] as const,
    permissionCheck: (...permissions: string[]) =>
      ['rbac-roles', 'permission-check', ...permissions] as const,
    /** Permissions */
    permissions: () => [...queryKeys.rbac.all, 'permissions'] as const,
    permissionsList: (params?: Record<string, unknown>) =>
      [...queryKeys.rbac.all, 'permissions', 'list', params ?? {}] as const,
  },
  /** RBAC Permissions (platform admin) */
  rbacPermissions: {
    all: ['rbac-permissions'] as const,
    search: (params: Record<string, unknown>) => ['rbac-permissions', 'search', params] as const,
  },
  /** RBAC Resources (platform admin) */
  resources: {
    all: ['resources'] as const,
    search: (params: Record<string, unknown>) => ['resources', 'search', params] as const,
    byModule: () => ['resources', 'by-module'] as const,
    modules: () => ['resources', 'modules'] as const,
    detail: (id: string) => ['resources', 'detail', id] as const,
  },
  screens: {
    all: ['screens'] as const,
    byModule: (params?: Record<string, unknown>) =>
      [...queryKeys.screens.all, 'by-module', params ?? {}] as const,
    subsidiary: (subsidiaryId: string) => ['screens', 'subsidiary', subsidiaryId] as const,
  },
  abac: {
    all: ['abac'] as const,
    policies: () => [...queryKeys.abac.all, 'policies'] as const,
    policyById: (id: string) => [...queryKeys.abac.all, 'policies', id] as const,
    attributesMe: () => [...queryKeys.abac.all, 'attributes', 'me'] as const,
    attributesByUser: (userId: string) => [...queryKeys.abac.all, 'attributes', userId] as const,
  },
  sso: {
    all: ['sso'] as const,
    config: () => ['sso', 'subsidiary'] as const,
    samlMetadata: () => ['sso', 'metadata'] as const,
  },
  /**
   * Legacy workflow-service experiments (`features/experiments`). Not the catalog-v2 screens —
   * those use `catalogService.experiments` / `catalogService.experiment(id)`; never mix the two.
   */
  experiments: {
    all: ['experiments'] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.experiments.all, 'list', params ?? {}] as const,
    /**
     * Full draft payload for the resume flow. Distinct from `detail` so the
     * listing/detail caches and the wizard hydration cache don't
     * cross-pollinate.
     */
    draft: (id: string) => [...queryKeys.experiments.all, 'draft', id] as const,
    stats: () => [...queryKeys.experiments.all, 'stats'] as const,
    detail: (id: string) => [...queryKeys.experiments.all, 'detail', id] as const,
    timeline: (id: string) => [...queryKeys.experiments.all, 'timeline', id] as const,
    /** `segment` is the encoded `?segment=` filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
    kpis: (id?: string, segment?: string) =>
      [...queryKeys.experiments.all, 'kpis', id, segment] as const,
    armComparison: (id: string, segment?: string) =>
      [...queryKeys.experiments.all, 'armComparison', id, segment] as const,
    roasComparison: (id: string, segment?: string) =>
      [...queryKeys.experiments.all, 'roasComparison', id, segment] as const,
    dailyLift: (id: string, segment?: string) =>
      [...queryKeys.experiments.all, 'dailyLift', id, segment] as const,
    inStockTrends: (id: string) => [...queryKeys.experiments.all, 'inStockTrends', id] as const,
    categoryExclusions: (id: string) =>
      [...queryKeys.experiments.all, 'categoryExclusions', id] as const,
    adjustedMetrics: (id: string) => [...queryKeys.experiments.all, 'adjustedMetrics', id] as const,
    categoryPerformance: (id: string, category: string, segment?: string) =>
      [...queryKeys.experiments.all, 'categoryPerformance', id, category, segment] as const,
    /** CSV report tiles for the category-mode Detailed Data Downloads grid. */
    dataDownloads: (id: string, segment?: string) =>
      [...queryKeys.experiments.all, 'dataDownloads', id, segment] as const,
    /** Polled content-scoring workflow status for an experiment. */
    contentScoringStatus: (id: string) =>
      [...queryKeys.experiments.all, 'content-scoring-status', id] as const,
    /** Aggregate quality scores from the latest content-scoring run. */
    contentScoringQualityScore: (id: string) =>
      [...queryKeys.experiments.all, 'content-scoring-quality-score', id] as const,
    /** Prefix over all paginated content-scoring result pages for an experiment.
     *  Invalidate this to refetch every page regardless of the active page/size. */
    contentScoringResultsAll: (id: string) =>
      [...queryKeys.experiments.all, 'content-scoring-results', id] as const,
    /** Paginated per-product scoring results for the Enrichment Overview "Preview Products" tab. */
    contentScoringResults: (id: string, page: number, size: number) =>
      [...queryKeys.experiments.contentScoringResultsAll(id), page, size] as const,
    /** Polled sample-enrichment workflow status for an experiment. */
    sampleEnrichmentStatus: (id: string) =>
      [...queryKeys.experiments.all, 'sample-enrichment-status', id] as const,
    /** Aggregate Before/After/Lift quality scores for the latest sample run. */
    sampleEnrichmentQualityScore: (id: string) =>
      [...queryKeys.experiments.all, 'sample-enrichment-quality-score', id] as const,
    /** Prefix over all paginated sample-enrichment preview pages for an experiment.
     *  Invalidate this to refetch every page regardless of the active page/size. */
    sampleEnrichmentPreviewAll: (id: string) =>
      [...queryKeys.experiments.all, 'sample-enrichment-preview', id] as const,
    /** Paginated per-product preview cards for the latest sample run. */
    sampleEnrichmentPreview: (id: string, page: number, size: number) =>
      [...queryKeys.experiments.sampleEnrichmentPreviewAll(id), page, size] as const,
    /** Polled experiment-pipeline status. Stops once the pipeline reaches a terminal state. */
    pipelineStatus: (id: string) => [...queryKeys.experiments.all, 'pipeline-status', id] as const,
    /** Read-only forecast of what the pipeline would enrich. Read on mount of the trigger UI; no polling. */
    pipelinePreview: (id: string) =>
      [...queryKeys.experiments.all, 'pipeline-preview', id] as const,
    /** Aggregate Before/After/Lift quality scores from the latest full-catalog pipeline run. */
    pipelineQualityScore: (id: string) =>
      [...queryKeys.experiments.all, 'pipeline-quality-score', id] as const,
    /** Prefix over all paginated pipeline enriched-product pages for an experiment. */
    pipelineEnrichedProductsAll: (id: string) =>
      [...queryKeys.experiments.all, 'pipeline-enriched-products', id] as const,
    /** Paginated enriched products from the completed experiment pipeline run. */
    pipelineEnrichedProducts: (id: string, page: number, size: number) =>
      [...queryKeys.experiments.pipelineEnrichedProductsAll(id), page, size] as const,
    /** Every server page of the run folded into one entry, for the listings that
     *  paginate client-side. Under `...All` so the prefix reset still covers it. */
    pipelineEnrichedProductsComplete: (id: string) =>
      [...queryKeys.experiments.pipelineEnrichedProductsAll(id), 'complete'] as const,
    sampleProduct: (params?: Record<string, unknown>) =>
      [...queryKeys.experiments.all, 'sample-product', params ?? {}] as const,
    catalog: (params?: Record<string, unknown>) =>
      [...queryKeys.experiments.all, 'catalog', params ?? {}] as const,
    catalogFilters: () => [...queryKeys.experiments.all, 'catalog-filters'] as const,
    contentAttributes: () => [...queryKeys.experiments.all, 'content-attributes'] as const,
  },
  /**
   * Analytics service (read-only experiment dashboards). Distinct from
   * `queryKeys.experiments.*` so the workflow-service CRUD caches and the
   * analytics-service aggregation caches stay independent.
   */
  analytics: {
    all: ['analytics'] as const,
    experiments: {
      all: ['analytics', 'experiments'] as const,
      list: (params?: Record<string, unknown>) =>
        ['analytics', 'experiments', 'list', params ?? {}] as const,
      summary: (id: string, segment?: string) =>
        ['analytics', 'experiments', 'summary', id, segment] as const,
      performanceTrend: (id: string, metric: string, segment?: string) =>
        ['analytics', 'experiments', 'performance-trend', id, metric, segment] as const,
      health: (id: string, params?: Record<string, unknown>) =>
        ['analytics', 'experiments', 'health', id, params ?? {}] as const,
      agentSummary: (id: string, params?: Record<string, unknown>) =>
        ['analytics', 'experiments', 'agent-summary', id, params ?? {}] as const,
      /** Polled per-arm assignment report (A/B/C summary + lift). Drives the Review Groups & Splits table. */
      armAssignmentReport: (id: string) =>
        ['analytics', 'experiments', 'arm-assignment-report', id] as const,
      /** Assigned products for a given arm/outlier filter combo (server-side filtered). */
      armAssignmentProducts: (id: string, filters?: Record<string, unknown>) =>
        ['analytics', 'experiments', 'arm-assignment-products', id, filters ?? {}] as const,
    },
  },
  chat: {
    all: ['chat'] as const,
    conversation: (id: string) => [...queryKeys.chat.all, id] as const,
  },
  /** Onboarding status — drives the post-login redirect between
   *  `/onboarding`, `/onboarding/complete-import` and `/experiments`. */
  onboarding: {
    all: ['onboarding'] as const,
    status: () => ['onboarding', 'status'] as const,
  },
  /** Connect links minted from the sharing panel, keyed by list filters. */
  connectLinks: {
    all: ['connect-links'] as const,
    list: (filters: object) => [...queryKeys.connectLinks.all, 'list', filters] as const,
  },
  /** Google OAuth grants (and their connections) behind connect links. */
  grants: {
    all: ['grants'] as const,
    list: (filters: object) => [...queryKeys.grants.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.grants.all, 'detail', id] as const,
  },
  /** Public `/connector/:token` reads — no tenant scope, keyed by token. */
  publicConnectLinks: {
    all: ['public-connect-links'] as const,
    detail: (token: string) => [...queryKeys.publicConnectLinks.all, 'detail', token] as const,
    customers: (token: string) =>
      [...queryKeys.publicConnectLinks.all, 'customers', token] as const,
  },
  connections: {
    all: ['connections'] as const,
    // `['connections', 'detail', id]` keeps the shape distinct from
    // sibling literals (`['connections', 'connected']`, `['connections',
    // 'metrics', id]`, etc.) so an id that happened to equal one of those
    // literals can't collide with another query's cache entry.
    // `subsidiaryId` scopes to a page-local subsidiary override (Integrations
    // pages); appended only when provided so existing callers keep their key.
    byId: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'detail',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /**
     * One recurring Temporal Schedule for one connection, scoped per subsidiary.
     * `scheduleType` is part of the key because a connection can own several
     * schedules (sync / coverage / reports / catalog) that describe different
     * cadences — sharing one cache entry would have them overwrite each other.
     * Appended only when provided so an invalidation may still pass just
     * `(id, subsidiaryId)` and prefix-match every type.
     */
    schedule: (id: string, subsidiaryId?: string, scheduleType?: string) =>
      [
        ...queryKeys.connections.all,
        'schedule',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
        ...(scheduleType ? [scheduleType] : []),
      ] as const,
    /** Upload destinations resolved by an existing connection's own handler —
     *  keyed per connection (not per merchant) because the addressing config
     *  lives on the connection, and it differs by connector (GMC merchant id vs
     *  Meta catalog id). */
    uploadTargets: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'upload-targets',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Supplemental data sources of an existing connection (locale destination picker). */
    supplementalDataSources: (id: string) =>
      [...queryKeys.connections.all, 'supplemental-data-sources', id] as const,
    /** Connect-mode Google Ads account picker — the accounts the signed-in
     *  user can access, once oauth/callback has completed for this connection. */
    googleAdsCustomers: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'google-ads-customers',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Resume-dispatch read of `POST /connections/:id/validate` — distinct
     *  from `useValidateConnection`'s mutation usage (confirmation screens'
     *  manual re-validate) since this one fires automatically on landing on a
     *  PENDING/ERROR connect-mode row and wants normal query loading/caching. */
    oauthValidation: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'oauth-validation',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Primary data sources for the GMC feed picker. `accountId` is part of the
     *  key because an MCA answers differently per sub-account (first call lists
     *  sub-accounts, second lists that account's feeds). Prefixed with a literal
     *  so a bare `(id, subsidiaryId)` invalidation still prefix-matches. */
    primaryFeeds: (id: string, accountId?: string | null, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'primary-feeds',
        id,
        ...(accountId ? ['account', accountId] : []),
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** The `shopify_export` config block for a GMC connection, scoped per
     *  page-local subsidiary override. */
    shopifyExportConfig: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'shopify-export-config',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** One directory listing on a connection's file transport. Keyed by path +
     *  pattern so navigating back to a directory is instant and re-browsing the
     *  same one after an upload still refetches on invalidate. */
    fileBrowse: (
      id: string,
      path: string,
      pattern?: string,
      recursive?: boolean,
      subsidiaryId?: string,
    ) =>
      [
        ...queryKeys.connections.all,
        'file-browse',
        id,
        path,
        ...(pattern ? [pattern] : []),
        // Part of the key: a recursive search and a plain listing of the same
        // path are different answers, and sharing one entry would show a
        // sub-folder's files as if they were in this one.
        ...(recursive ? ['recursive'] : []),
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Paged ("Load more") listing of one directory — a separate entry from
     *  `fileBrowse` because an infinite query's cache holds pages, not one listing. */
    fileBrowsePaged: (id: string, path: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'file-browse-paged',
        id,
        path,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Adobe report suites reachable by one connection's credential. */
    adobeReportSuites: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'adobe-report-suites',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** SFTP extraction wizard progress for a connection (selected directory,
     *  per-report mapping state, blockers). Invalidated after every save and
     *  validate — a stamp cleared server-side is not visible any other way. */
    extractionStatus: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'extraction-status',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** The report picker's list (all nine reports with their configured /
     *  validated / enabled state). Separate key from `extractionStatus`: a
     *  validate stamps one report, which changes the derived `enabled` set. */
    extractionReports: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'extraction-reports',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Subsidiary-wide scheduled-jobs list, scoped per page-local subsidiary. */
    schedulesList: (subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'schedules-list',
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    // `subsidiaryId` scopes to a page-local subsidiary override (Integrations
    // pages); appended only when provided so existing callers keep their key.
    connected: (subsidiaryId?: string) =>
      [...queryKeys.connections.all, 'connected', ...(subsidiaryId ? [subsidiaryId] : [])] as const,
    // Full-record connections listing. `statuses` is part of the key so lists
    // fetched with different status filters (e.g. ACTIVE-only vs ACTIVE+PAUSED)
    // keep separate cache entries; sorted + joined so key identity doesn't
    // depend on caller argument order.
    // `subsidiaryId` scopes the list to a specific subsidiary (the platform
    // Catalog Images page's page-local override). Appended only when provided
    // so existing callers keep their original key identity.
    list: (statuses?: readonly string[], subsidiaryId?: string, category?: string) =>
      [
        ...queryKeys.connections.all,
        'list',
        statuses?.length ? [...statuses].sort().join(',') : 'all',
        ...(category ? [category] : []),
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    // Active pipeline workflow for a connection, scoped by connector code so a
    // future multi-connector connection keeps distinct cache entries.
    activeWorkflow: (id: string, connectorCode: string) =>
      [...queryKeys.connections.all, 'active-workflow', id, connectorCode] as const,
    /** Connector capability matrix for a connection (`GET /connections/{id}/capabilities`). */
    capabilities: (id: string) => [...queryKeys.connections.all, 'capabilities', id] as const,
    /** Latest coverage snapshot for a connection; without `market` also the prefix of every
     *  coverage key of the connection, so one invalidation refreshes them all. */
    coverage: (id: string, market?: string | null) =>
      market
        ? ([...queryKeys.connections.all, 'coverage', id, 'market', market] as const)
        : ([...queryKeys.connections.all, 'coverage', id] as const),
    /** Coverage trend; `params` folds the window/limit query into the key. */
    coverageHistory: (id: string, params: Record<string, unknown>) =>
      [...queryKeys.connections.all, 'coverage', id, 'history', params] as const,
    /** Per-product coverage drill-down page. */
    coverageProducts: (id: string, params: Record<string, unknown>) =>
      [...queryKeys.connections.all, 'coverage', id, 'products', params] as const,
    /** Coverage insights (what to do next). Kept under the `coverage` prefix so one
     *  `coverage(id)` invalidation refreshes the snapshot, history, products AND insights. */
    /** The subsidiary's per-connection coverage summary (Deliveries' connections strip). */
    coverageSummary: () => [...queryKeys.connections.all, 'coverage-summary'] as const,
    /** Progress of the coverage run in flight; under the `coverage` prefix like the rest. */
    coverageProgress: (id: string) =>
      [...queryKeys.connections.all, 'coverage', id, 'progress'] as const,
    coverageInsights: (id: string, market?: string | null) =>
      [...queryKeys.connections.all, 'coverage', id, 'insights', market ?? null] as const,
    /** The coverage redeliver job of the connection's latest finished coverage run. Under the
     *  `coverage` prefix too: a new snapshot can mean a new run, and so another job. */
    coverageRedeliver: (id: string) =>
      [...queryKeys.connections.all, 'coverage', id, 'redeliver'] as const,
    /** Connection audit trail (`POST /connections/{id}/events/list`), keyed by filter + page. */
    events: (id: string, filters: Record<string, unknown>, page: number, size: number) =>
      [...queryKeys.connections.all, 'events', id, filters, page, size] as const,
    ga4Properties: (id: string) => [...queryKeys.connections.all, 'ga4-properties', id] as const,
    ga4DataStreams: (id: string, propertyId: string) =>
      [...queryKeys.connections.all, 'ga4-data-streams', id, propertyId] as const,
    /** Account-level metrics from the connector (GMC totals + issues). */
    metrics: (id: string) => [...queryKeys.connections.all, 'metrics', id] as const,
    /** Generic (connector-agnostic) account-metrics snapshot for the connection
     *  detail drawer. Distinct key from `metrics` so its raw-record shape never
     *  collides with the typed per-connector metrics hooks. */
    metricsSnapshot: (id: string) =>
      [...queryKeys.connections.all, 'metrics-snapshot', id] as const,
    // Aggregate health across all connections. Lives under the `connections`
    // root so `invalidateQueries({ queryKey: connections.all })` from any
    // connection mutation also refreshes the health badges.
    health: (subsidiaryId?: string) =>
      [...queryKeys.connections.all, 'health', ...(subsidiaryId ? [subsidiaryId] : [])] as const,
    // Per-connection health detail (component breakdown), keyed by id.
    healthById: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.connections.all,
        'health',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
  },
  /** Service-level health dashboard (Integrations → Health). Distinct root from
   *  `connections.health` — this probes backend gateways, not per-connection
   *  credential health. */
  serviceHealth: {
    all: ['service-health'] as const,
    /** Per-service health, keyed by id — each card queries independently, and
     *  invalidating `all` (prefix) refreshes every one. */
    service: (id: string) => ['service-health', 'service', id] as const,
    /** Background cold-start warm-up poll. Deliberately its own key, NOT under
     *  `all`: the Health page invalidates `all` to refresh its cards, and the
     *  warm-up must not be dragged along by (or drag along) that refresh. */
    warmup: () => ['service-health', 'warmup'] as const,
  },
  /** Data Lake browser. `browse` is keyed by path so each tree level caches
   *  independently; `preview` by the file's full datalake path. */
  datalake: {
    all: ['datalake'] as const,
    // `subsidiaryId` scopes to a page-local subsidiary override; appended only
    // when provided so existing callers keep their key identity.
    browse: (path: string, subsidiaryId?: string) =>
      [...queryKeys.datalake.all, 'browse', path, ...(subsidiaryId ? [subsidiaryId] : [])] as const,
    preview: (source: string, subTree: string, path: string, subsidiaryId?: string) =>
      [
        ...queryKeys.datalake.all,
        'preview',
        source,
        subTree,
        path,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
  },
  /** Read-only ClickHouse table browser. `tables` caches the picker list;
   *  `schema`/`distinct` key by table (and column); `rows` keys by the full
   *  query (filters/sort/page) so each distinct view caches independently. */
  clickhouse: {
    all: ['clickhouse'] as const,
    /** Every key is subsidiary-scoped: reads are filtered by X-Subsidiary-ID
     *  server-side, so omitting it would serve one subsidiary's rows for
     *  another after switching scope. */
    tables: (subsidiaryId: string | undefined, connector?: string) =>
      [...queryKeys.clickhouse.all, 'tables', subsidiaryId ?? null, connector ?? null] as const,
    schema: (subsidiaryId: string | undefined, table: string) =>
      [...queryKeys.clickhouse.all, 'schema', subsidiaryId ?? null, table] as const,
    rows: (subsidiaryId: string | undefined, table: string, query: unknown) =>
      [...queryKeys.clickhouse.all, 'rows', subsidiaryId ?? null, table, query] as const,
    distinct: (subsidiaryId: string | undefined, table: string, column: string) =>
      [...queryKeys.clickhouse.all, 'distinct', subsidiaryId ?? null, table, column] as const,
  },
  /** Sentinel keys for disabled queries — must live OUTSIDE the feature's
   *  invalidation root so `invalidateQueries` doesn't sweep them up
   *  (TanStack invalidation is prefix-matched). Isolation is enforced by
   *  the unit test in `use-connections.test.tsx`. */
  disabledDetail: {
    connection: () => ['__disabled__', 'connection', 'detail'] as const,
  },
  /** Sync (unified connector data operations) — page-token scoped.
   *  `filterDisapproved` is part of the key so the scoped-import "only
   *  disapproved" filter caches its own cursor chain, distinct from the
   *  default listing. */
  sync: {
    all: ['sync'] as const,
    /** `pageSize` is required — the Step-2 pager lets the user change it, so a
     *  default here would silently build a key no live query actually uses. */
    productsList: (
      connectionId: string,
      pageToken: string | null,
      filterDisapproved: boolean,
      pageSize: number,
    ) =>
      ['sync', 'products', 'list', connectionId, pageToken, filterDisapproved, pageSize] as const,
  },
  /** Temporal workflow polling — status tracked by workflow_id. */
  workflows: {
    all: ['workflows'] as const,
    status: (workflowId: string) => ['workflows', 'status', workflowId] as const,
  },
  /** Catalog-import run chain + per-page ledger (onboarding Complete Import).
   *  Keyed by `pipeline_runs.id`; `pages` also by its filter/offset/limit. */
  ingestRuns: {
    all: ['ingest-runs'] as const,
    lineage: (runId: string) => ['ingest-runs', 'lineage', runId] as const,
    pagesAll: (runId: string) => ['ingest-runs', 'pages', runId] as const,
    pages: (runId: string, params: { status: string; offset: number; limit: number }) =>
      ['ingest-runs', 'pages', runId, params] as const,
  },
  /** Analytics-sync ingestion polling — dedicated namespace so the camelCase
   *  analytics status payload never shares a cache slot with the snake_case
   *  catalog-import `workflows.status` entries. Keyed by workflow_id. */
  analyticsSync: {
    all: ['analytics-sync'] as const,
    status: (workflowId: string) => ['analytics-sync', 'status', workflowId] as const,
  },
  /** Keyword Planner read caches. Connection-scoped — `connectionId` is
   *  required on all keys since the backend contract makes it a mandatory
   *  URL path param. */
  keywords: {
    all: ['keywords'] as const,
    list: (connectionId: string, params: Record<string, unknown>) =>
      ['keywords', 'list', connectionId, params] as const,
    detail: (connectionId: string, text: string, subsidiaryId?: string) =>
      ['keywords', 'detail', connectionId, text, subsidiaryId ?? null] as const,
  },
  /** Agent-services catalogues served by the agents themselves (baked into the image). */
  agents: {
    all: ['agents'] as const,
    /** Meta attribute catalogue; keyed by the pass-through toggle since it changes the row set. */
    metaCatalogAttributes: (includePassThrough: boolean) =>
      [...queryKeys.agents.all, 'meta-catalog-attributes', includePassThrough] as const,
    /** Google curated content-attribute taxonomy; keyed by the selectable toggle since it changes the row set. */
    googleContentAttributes: (selectableOnly: boolean) =>
      [...queryKeys.agents.all, 'google-content-attributes', selectableOnly] as const,
  },
  /** Config service — KPI + content-attribute taxonomies for create-experiment step 4. */
  config: {
    all: ['config'] as const,
    kpis: (platform: string) => ['config', 'kpis', platform] as const,
    contentAttributes: () => ['config', 'content-attributes'] as const,
    customerConstraints: () => ['config', 'customer-constraints'] as const,
    restricted: () => ['config', 'restricted'] as const,
    brandPreferences: () => ['config', 'brand-preferences'] as const,
    /** Scoped by platformId so global and per-platform cache slots stay isolated. */
    guidelinesDocument: (platformId?: string) =>
      ['config', 'guidelines-document', platformId ?? 'global'] as const,
    syncSchedule: () => ['config', 'sync-schedule'] as const,
  },
  /** Grouped product variants per connection — create-experiment step 1. */
  products: {
    all: ['products'] as const,
    variants: (connectionId: string, matchBy: string) =>
      ['products', 'variants', connectionId, matchBy] as const,
    variantDetail: (connectionId: string, matchValue: string, matchBy: string) =>
      ['products', 'variant-detail', connectionId, matchValue, matchBy] as const,
    /** Scoped by connection + the full request params so filter/sort/pagination changes refetch cleanly. */
    list: (connectionId: string, params: Record<string, unknown>) =>
      ['products', 'list', connectionId, params] as const,
    /** Infinite (card-view) product list — scoped by connection + request params
     *  (minus offset, which is the page cursor) so filter/sort changes refetch. */
    infiniteList: (connectionId: string, params: Record<string, unknown>) =>
      ['products', 'infinite-list', connectionId, params] as const,
    /** Eligibility waterfall — scoped by connection + request params (filters/syncId) so filter changes refetch. */
    eligibilityBreakdown: (connectionId: string, params: Record<string, unknown>) =>
      ['products', 'eligibility-breakdown', connectionId, params] as const,
    /** Column metadata scoped per connector (e.g. GMC vs future Meta schema).
     *  Resolves to a bare `ProductColumn[]` (experiments/content-enrichment). */
    columns: (connectorCode: string) => ['products', 'columns', connectorCode] as const,
    /** Catalog-explorer column metadata — distinct key from {@link columns} because
     *  this variant resolves the full response envelope and is subsidiary-scoped;
     *  sharing the `columns` key caused a shape-collision crash in the columns drawer. */
    catalogColumns: (connectorCode: string, subsidiaryId?: string) =>
      ['products', 'catalog-columns', connectorCode, subsidiaryId ?? null] as const,
    /** Catalog search field options — static per connector. */
    searchFields: (connectorCode: string, subsidiaryId?: string) =>
      ['products', 'search-fields', connectorCode, subsidiaryId ?? null] as const,
    /** Dynamic filter definitions — scoped by connection + sync + currently-applied
     *  filters (refetches when the selected sync/revision changes or as the user narrows). */
    filters: (
      connectionId: string,
      applied: Record<string, unknown>,
      syncId: string | null = null,
    ) => ['products', 'filters', connectionId, applied, syncId] as const,
    /** Paginated option values for one filter key — scoped by connection + key + search + applied + sync. */
    filterOptions: (
      connectionId: string,
      key: string,
      search: string,
      applied: Record<string, unknown>,
      syncId: string | null = null,
    ) => ['products', 'filter-options', connectionId, key, search, applied, syncId] as const,
    /** Platform product catalog — static per deploy, used by org-onboarding enrollment step. */
    catalog: () => ['products', 'catalog'] as const,
    /** Enrolled products for a subsidiary, keyed by subsidiary ID. */
    bySubsidiary: (subsidiaryId: string) => ['products', 'subsidiary', subsidiaryId] as const,
    /** Deterministic suggestions for the AI product-filter panel, scoped per connection + surface type. */
    aiFilterSuggestions: (connectionId: string, type: string) =>
      ['products', 'ai-filter-suggestions', connectionId, type] as const,
    /** Paged, searchable group list for the Step-1 AI grouping dropdown — scoped by connection + request context. */
    aiFilterVariantGroups: (connectionId: string, params: Record<string, unknown>) =>
      ['products', 'ai-filter-variant-groups', connectionId, params] as const,
    /** Paged AI-filtered product list (Step-2 AI mode) — scoped by connection + conversation/pagination context. */
    aiFilterList: (connectionId: string, params: Record<string, unknown>) =>
      ['products', 'ai-filter-list', connectionId, params] as const,
    /** Full detail for a single product — scoped by connection + external id. */
    detail: (connectionId: string, externalId: string) =>
      ['products', 'detail', connectionId, externalId] as const,
    /** Ingestion revisions for a connection — feeds the revision selector. */
    revisions: (connectionId: string) => ['products', 'revisions', connectionId] as const,
    /** Dynamic filter catalog (Products page) — scoped by connector + connection +
     *  sync + the applied filters so narrowing refetches vendor options cleanly. */
    filterCatalog: (
      connectorCode: string,
      connectionId: string,
      syncId: string | null | undefined,
      applied: Record<string, unknown>,
    ) =>
      ['products', 'filter-catalog', connectorCode, connectionId, syncId ?? null, applied] as const,
    /** Connector-specific grouping-method options (Products page). */
    defaultGroupingConfig: (connectorCode: string) =>
      ['products', 'default-grouping-config', connectorCode] as const,
    /** Category breakdown — scoped by connection + page window + request params. */
    categories: (
      connectionId: string,
      offset: number,
      limit: number,
      params: Record<string, unknown>,
    ) => ['products', 'categories', connectionId, offset, limit, params] as const,
    /** Item-group summaries — scoped by connection + page window + request params. */
    itemGroups: (
      connectionId: string,
      offset: number,
      limit: number,
      params: Record<string, unknown>,
    ) => ['products', 'item-groups', connectionId, offset, limit, params] as const,
    /** Parent + children for one item group — scoped by connection + group + window + params. */
    itemGroupChildren: (
      connectionId: string,
      itemGroupId: string,
      offset: number,
      limit: number,
      params: Record<string, unknown>,
    ) =>
      [
        'products',
        'item-group-children',
        connectionId,
        itemGroupId,
        offset,
        limit,
        params,
      ] as const,
  },
  /**
   * Data Quality dashboard (Integrations → Data Quality). Keyed under
   * `data-quality`; `all` is the invalidation root after a sync completes.
   */
  dataQuality: {
    all: ['data-quality'] as const,
    /** Summary KPIs — scoped by subsidiary + connection + sync. */
    summary: (connectionId: string, syncId?: string | null, subsidiaryId?: string | null) =>
      ['data-quality', 'summary', subsidiaryId ?? null, connectionId, syncId ?? null] as const,
    /** Rich overview — scoped by subsidiary + connection + sync + ingestion date + applied filters. */
    overview: (
      connectionId: string,
      syncId?: string | null,
      ingestionDate?: string | null,
      filters?: unknown,
      subsidiaryId?: string | null,
    ) =>
      [
        'data-quality',
        'overview',
        subsidiaryId ?? null,
        connectionId,
        syncId ?? null,
        ingestionDate ?? null,
        filters ?? null,
      ] as const,
    /** Product list — scoped by subsidiary + connection + page + request params. */
    products: (
      connectionId: string,
      page: number,
      params?: Record<string, unknown>,
      subsidiaryId?: string | null,
    ) =>
      [
        'data-quality',
        'products',
        subsidiaryId ?? null,
        connectionId,
        page,
        params ?? null,
      ] as const,
    /** Top field issues — scoped by subsidiary + connection + sync + limit. */
    issues: (
      connectionId: string,
      syncId: string | null,
      limit: number,
      subsidiaryId?: string | null,
    ) => ['data-quality', 'issues', subsidiaryId ?? null, connectionId, syncId, limit] as const,
    /** Trend points — scoped by subsidiary + connection + date window + sync. */
    trends: (
      connectionId: string,
      startDate: string,
      endDate: string,
      syncId?: string | null,
      subsidiaryId?: string | null,
    ) =>
      [
        'data-quality',
        'trends',
        subsidiaryId ?? null,
        connectionId,
        startDate,
        endDate,
        syncId ?? null,
      ] as const,
    /** Live sync workflow status — scoped by workflow id. */
    syncStatus: (workflowId: string) => ['data-quality', 'sync-status', workflowId] as const,
    /** Single-product scorecard — scoped by subsidiary + connection + external id. */
    productDetail: (connectionId: string, externalId: string, subsidiaryId?: string | null) =>
      ['data-quality', 'product-detail', subsidiaryId ?? null, connectionId, externalId] as const,
    /** Single-product per-sync history — scoped by subsidiary + connection + external id. */
    productHistory: (connectionId: string, externalId: string, subsidiaryId?: string | null) =>
      ['data-quality', 'product-history', subsidiaryId ?? null, connectionId, externalId] as const,
    /** Sync-to-sync comparison — scoped by subsidiary + connection + before/after sync ids. */
    comparison: (
      connectionId: string,
      beforeSyncId: string,
      afterSyncId: string,
      subsidiaryId?: string | null,
    ) =>
      [
        'data-quality',
        'comparison',
        subsidiaryId ?? null,
        connectionId,
        beforeSyncId,
        afterSyncId,
      ] as const,
  },
  /** Sync write operations (capabilities + product update/delete) for the
   *  Products page. Separate namespace so mutations can invalidate write-side
   *  caches without touching the read-side `products` list entries. */
  syncOps: {
    all: ['sync-ops'] as const,
    /** Write capabilities for a connection — keyed by connection id. */
    capabilities: (connectionId: string) => ['sync-ops', 'capabilities', connectionId] as const,
  },
  /**
   * Connectors are paginated + filterable; including the request params in the
   * key scopes cache per filter/pagination permutation (TanStack Query compares
   * keys structurally, so object identity does not matter).
   */
  connectors: {
    all: ['connectors'] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.connectors.all, 'list', params ?? {}] as const,
    /** Connector-specific default grouping options (create-experiment step 1). */
    defaultGroupingConfig: (connectorCode: string) =>
      [...queryKeys.connectors.all, 'default-grouping-config', connectorCode] as const,
    /** Static placeholder legend for a connector's output file name. */
    fileNaming: (connectorCode: string) =>
      [...queryKeys.connectors.all, 'file-naming', connectorCode] as const,
    /** Validation + rendered example for one candidate file name. */
    fileNamePreview: (connectorCode: string, template: string) =>
      [...queryKeys.connectors.all, 'file-naming', connectorCode, 'preview', template] as const,
    /** Upload targets for a connector addressed by merchant id (onboarding SFTP
     *  target picker) — keyed per connector + merchant so each merchant caches
     *  independently. */
    uploadTargets: (connectorCode: string, merchantId: string) =>
      [...queryKeys.connectors.all, 'upload-targets', connectorCode, merchantId] as const,
    /** Supplemental data sources by merchant id, before a connection exists. */
    supplementalDataSources: (connectorCode: string, merchantId: string) =>
      [
        ...queryKeys.connectors.all,
        'supplemental-data-sources',
        connectorCode,
        merchantId,
      ] as const,
  },
  /**
   * Integration pipelines — list is scoped by filters + pagination so each
   * permutation caches independently; run history keyed by pipeline + page.
   */
  pipelines: {
    all: ['pipelines'] as const,
    // `subsidiaryId` scopes to a page-local subsidiary override; appended only
    // when provided so existing callers keep their key identity.
    list: (filters: Record<string, unknown>, page: number, size: number, subsidiaryId?: string) =>
      [
        ...queryKeys.pipelines.all,
        'list',
        filters,
        page,
        size,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    detail: (id: string, subsidiaryId?: string) =>
      [...queryKeys.pipelines.all, 'detail', id, ...(subsidiaryId ? [subsidiaryId] : [])] as const,
    runs: (id: string, page: number, size: number, subsidiaryId?: string) =>
      [
        ...queryKeys.pipelines.all,
        'runs',
        id,
        page,
        size,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    latestRun: (id: string, subsidiaryId?: string) =>
      [
        ...queryKeys.pipelines.all,
        'latest-run',
        id,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    // Run + event keys carry the page-local subsidiary scope for the same reason
    // as `list`/`runs`: the same run id read under two scopes is two different
    // server responses (one 404s), so they must not share a cache slot.
    run: (runId: string, subsidiaryId?: string) =>
      [...queryKeys.pipelines.all, 'run', runId, ...(subsidiaryId ? [subsidiaryId] : [])] as const,
    runEvents: (runId: string, subsidiaryId?: string) =>
      [
        ...queryKeys.pipelines.all,
        'run-events',
        runId,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** "Load more" feed of a run's events in a server order. Nested under
     *  `runEvents(runId)` so invalidating that prefix refreshes the feed too. */
    runEventsFeed: (runId: string, order: 'asc' | 'desc', subsidiaryId?: string) =>
      [
        ...queryKeys.pipelines.all,
        'run-events',
        runId,
        'feed',
        order,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
  },
  /** Deliveries — read/analytics for the delivery branch of pipeline detail. */
  deliveries: {
    all: ['deliveries'] as const,
    plan: (connectionId: string, request: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'plan', connectionId, request] as const,
    planProducts: (connectionId: string, request: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'plan-products', connectionId, request] as const,
    deliverableFields: (connectionId: string) =>
      [...queryKeys.deliveries.all, 'deliverable-fields', connectionId] as const,
    planProductDetail: (connectionId: string, request: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'plan-product-detail', connectionId, request] as const,
    latestBySource: (connectionId: string, workflowIds: readonly string[]) =>
      [...queryKeys.deliveries.all, 'latest-by-source', connectionId, workflowIds] as const,
    batchJob: (connectionId: string, jobId: string) =>
      [...queryKeys.deliveries.all, 'batch-job', connectionId, jobId] as const,
    summary: (connectionId: string, startDate?: string | null, endDate?: string | null) =>
      [
        ...queryKeys.deliveries.all,
        'summary',
        connectionId,
        startDate ?? null,
        endDate ?? null,
      ] as const,
    jobs: (connectionId: string, filters: Record<string, unknown>, limit: number, offset: number) =>
      [...queryKeys.deliveries.all, 'jobs', connectionId, filters, limit, offset] as const,
    jobProducts: (
      connectionId: string,
      jobId: string,
      filters: Record<string, unknown>,
      limit: number,
      offset: number,
    ) =>
      [
        ...queryKeys.deliveries.all,
        'job-products',
        connectionId,
        jobId,
        filters,
        limit,
        offset,
      ] as const,
    /** Upload-driven supplemental delivery runs, keyed by filter + page + subsidiary scope. */
    supplementalRuns: (
      filters: Record<string, unknown>,
      page: number,
      size: number,
      subsidiaryId?: string,
    ) =>
      [
        ...queryKeys.deliveries.all,
        'supplemental-runs',
        filters,
        page,
        size,
        subsidiaryId ?? null,
      ] as const,
    /** One product's audit row within a job. */
    jobProductDetail: (connectionId: string, jobId: string, productId: string) =>
      [...queryKeys.deliveries.all, 'job-product', connectionId, jobId, productId] as const,
    /**
     * Delivery Runs console — one subtree under `runs/<runId>` so any control
     * action can invalidate everything for a run at once (or the whole group via
     * `exportProfiles.all`). Filter/pagination params are folded into each key so
     * every permutation caches independently; cursors are NOT keyed (infinite
     * pages accumulate under the products key).
     */
    runsList: (filters: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'runs', 'list', filters] as const,
    run: (runId: string) => [...queryKeys.deliveries.all, 'runs', runId, 'detail'] as const,
    runTree: (runId: string, opts: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'runs', runId, 'tree', opts] as const,
    runUnits: (runId: string, page: number, size: number) =>
      [...queryKeys.deliveries.all, 'runs', runId, 'units', page, size] as const,
    runUnitProducts: (runId: string, unitIndex: number, filters: Record<string, unknown>) =>
      [
        ...queryKeys.deliveries.all,
        'runs',
        runId,
        'units',
        unitIndex,
        'products',
        filters,
      ] as const,
    runProducts: (runId: string, filters: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'runs', runId, 'products', filters] as const,
    /** Static outcome filter menu shared by every run (no run id). */
    productOutcomes: () => [...queryKeys.deliveries.all, 'product-outcomes'] as const,
    /** Batch product lookup on a connection, keyed by the request body. */
    productsLookup: (connectionId: string, body: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'products-lookup', connectionId, body] as const,
    /** Cause-grouped blocked-products worklist, keyed by the request body. */
    blockedCauses: (connectionId: string, body: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'blocked-causes', connectionId, body] as const,
    /** One cause's blocked-product drill-down page. */
    blockedProducts: (connectionId: string, body: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'blocked-products', connectionId, body] as const,
    /** One job's resolution reconciliation, keyed by its query (headline tallies
     *  don't move with `resolution`, but the cache still separates them so a
     *  filtered list never flashes stale rows from another filter). */
    jobReconciliation: (connectionId: string, jobId: string, query: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'job-reconciliation', connectionId, jobId, query] as const,
    runTimeline: (runId: string) =>
      [...queryKeys.deliveries.all, 'runs', runId, 'timeline'] as const,
    runLineage: (runId: string) => [...queryKeys.deliveries.all, 'runs', runId, 'lineage'] as const,
    runReport: (runId: string) => [...queryKeys.deliveries.all, 'runs', runId, 'report'] as const,
    runReconcile: (runId: string, includeClickhouse: boolean) =>
      [...queryKeys.deliveries.all, 'runs', runId, 'reconcile', includeClickhouse] as const,
    /**
     * Meta delivery workspace — one product's cross-job audit timeline on a
     * connection. `params` (idType/limit/offset/includePayload) fold into the key
     * so each window caches independently.
     */
    productTimeline: (connectionId: string, productId: string, params: Record<string, unknown>) =>
      [...queryKeys.deliveries.all, 'product-timeline', connectionId, productId, params] as const,
  },
  catalogImages: {
    all: ['catalog-images'] as const,
    /** Paginated image list per connection — filter object scoped per
     *  permutation so each connection/filter/page combination caches separately.
     *  `subsidiaryId` (page-local override) is appended only when provided so a
     *  subsidiary switch refetches instead of serving another subsidiary's rows. */
    list: (connectionId: string, filters?: Record<string, unknown>, subsidiaryId?: string) =>
      [
        ...queryKeys.catalogImages.all,
        'list',
        connectionId,
        filters ?? {},
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
    /** Sync history for a connection — feeds the sync picker. `subsidiaryId`
     *  (page-local override) appended only when provided. */
    syncs: (connectionId: string, subsidiaryId?: string) =>
      [
        ...queryKeys.catalogImages.all,
        'syncs',
        connectionId,
        ...(subsidiaryId ? [subsidiaryId] : []),
      ] as const,
  },
  catalogOperations: {
    all: ['catalog-operations'] as const,
    /** Listing-page summary cards + alert-banner aggregates. */
    stats: () => [...queryKeys.catalogOperations.all, 'stats'] as const,
    /** Paginated batch listing — filter object scoped per permutation so each
     *  tab/search/page combination gets its own cache slot. */
    batches: (filters: Record<string, unknown>) =>
      [...queryKeys.catalogOperations.all, 'batches', filters] as const,
    /** Per-status tab-strip counts — filter object scoped per permutation so
     *  it invalidates together with `batches` on a filter change. */
    statusCounts: (filters: Record<string, unknown>) =>
      [...queryKeys.catalogOperations.all, 'status-counts', filters] as const,
    /** Single batch detail keyed on the job uuid (not the human batchId). */
    batchDetail: (jobId: string) => [...queryKeys.catalogOperations.all, 'batches', jobId] as const,
    /** Phase 1 (shell) header, keyed on the job uuid. Nested under `batches`/jobId
     *  so the page-level `catalogOperations.all` invalidation still refetches it. */
    batchHeader: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'header'] as const,
    /** Phase 2 (live progress) stage counters, keyed on the job uuid. */
    batchLiveStage: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'live-stage'] as const,
    /** Phase 3 coverage tabs, keyed on the job uuid. Distinct from
     *  `batchCoverage(workflowId)` below (the Coverage *Signals* cards keyed on
     *  the workflow slug) — different resource despite the shared word. */
    batchCoverageDetail: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'coverage-detail'] as const,
    /** Phase 4 (heaviest, lazy) optimization quality, keyed on the job uuid. */
    batchOptimizationQuality: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'optimization-quality'] as const,
    /** Batch Details Funnel, keyed on the job uuid; nested under `batches`/jobId
     *  so page-level invalidation refetches it with the rest of the page. */
    batchFunnel: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'funnel'] as const,
    /** Funnel drill-in product page — query object scopes each stage/outcome/reason/page. */
    batchFunnelProducts: (jobId: string, query: Record<string, unknown>) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'funnel', 'products', query] as const,
    /** Upstream listing behind a funnel drill-in (curation / integrations `productsPath`). */
    batchFunnelUpstream: (jobId: string, path: string, query: Record<string, unknown>) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        jobId,
        'funnel',
        'upstream',
        path,
        query,
      ] as const,
    /** Stage errors for the batch, keyed on the job uuid. */
    batchStageErrors: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'stage-errors'] as const,
    /** Batch runs (root + retries), keyed on the job uuid under `batches`/jobId. */
    batchRuns: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'runs'] as const,
    /** Carry-over between batches (carried in / moved out / balance), under `batches`/jobId. */
    batchCarryover: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'carryover'] as const,
    /** One retry run's products — query scopes outcome/page. */
    batchRunProducts: (jobId: string, runId: string, query: Record<string, unknown>) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        jobId,
        'runs',
        runId,
        'products',
        query,
      ] as const,
    /** Every attempt at one product of the batch. */
    batchProductAttempts: (jobId: string, productId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'attempts', productId] as const,
    /** Classified Needs Attention for the batch. */
    batchNeedsAttention: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'needs-attention'] as const,
    /** Job-detail `postSteps` (SD/PD health), under `batches`/jobId. */
    batchPostSteps: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'post-steps'] as const,
    /** Product journey runs for one step, under `batches`/jobId. */
    journeyRuns: (jobId: string, step: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'journey', 'runs', step] as const,
    /** One journey run's products in a step — query scopes class/q/page. */
    journeyRunProducts: (
      jobId: string,
      runId: string,
      step: string,
      query: Record<string, unknown>,
    ) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        jobId,
        'journey',
        'runs',
        step,
        runId,
        'products',
        query,
      ] as const,
    /** Dry-run counts for the post-step retry dialog, one per scope. */
    postStepDryRun: (jobId: string, steps: string[], scope: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        jobId,
        'journey',
        'dry-run',
        steps,
        scope,
      ] as const,
    /** One product's post-step attempts. */
    postStepAttempts: (jobId: string, productId: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        jobId,
        'post-step-attempts',
        productId,
      ] as const,
    /** One product's funnel trace. */
    batchFunnelTrace: (jobId: string, productId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'funnel', 'trace', productId] as const,
    /** AI-Performance recompute status, keyed on the workflow slug
     *  (`BatchHeader.batchId`), like `batchInsights` — not the job uuid. */
    batchInsightsStatus: (workflowId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', workflowId, 'insights-status'] as const,
    /** Performance Signals tab data, keyed on the workflow id
     *  (`BatchDetail.batchId`), not the job uuid. */
    batchInsights: (workflowId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', workflowId, 'insights'] as const,
    /** Insight-membership drawer (Product Term / Occasion / Popular Attribute
     *  / Macro or Micro Trend row drill-down) — sibling of `batchInsights`,
     *  keyed by category + label + active filters. `page` excluded: it's an
     *  infinite query, and page is the `pageParam`, not part of the cache
     *  identity. */
    insightMembershipProducts: (
      workflowId: string,
      category: string,
      label: string,
      group: string,
      productCategory: string,
    ) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        workflowId,
        'insights',
        'membership-products',
        category,
        label,
        group,
        productCategory,
      ] as const,
    /** Enrichment Coverage tab signal cards, keyed on the workflow id
     *  (`BatchDetail.batchId`) like `batchInsights`, not the job uuid. */
    batchCoverage: (workflowId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', workflowId, 'coverage'] as const,
    /** Prefix over all post-enrichment validation reads of one run (its root id), under
     *  `batches` so the page refresh covers it; invalidated after a revalidate / re-enrich. */
    enrichmentValidationAll: (workflowId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', workflowId, 'enrichment-validation'] as const,
    /** Validation banner summary (status + counts) for one run. */
    enrichmentValidationSummary: (workflowId: string) =>
      [...queryKeys.catalogOperations.enrichmentValidationAll(workflowId), 'summary'] as const,
    /** Counts over one product list of a run (`digest` identifies the list). Under the run's
     *  prefix, so every re-run / fix that invalidates the run refreshes these too. */
    enrichmentValidationProductsSummary: (workflowId: string, digest: string) =>
      [
        ...queryKeys.catalogOperations.enrichmentValidationAll(workflowId),
        'products-summary',
        digest,
      ] as const,
    /** A cross-run products list (`digest` identifies the scopes) — pages and facets under it.
     *  Read fresh on every view (staleTime 0), so a fix in any run shows on return. */
    enrichmentValidationSearch: (digest: string) =>
      [...queryKeys.catalogOperations.enrichmentValidationSearchAll(), digest] as const,
    /** Prefix over every cross-run list — invalidated by any run's re-run / fix. */
    enrichmentValidationSearchAll: () =>
      [...queryKeys.catalogOperations.all, 'enrichment-validation-search'] as const,
    /** Prefix over every View-products page of one run, whatever its run state, filters, page. */
    enrichmentValidationProductsAll: (workflowId: string) =>
      [...queryKeys.catalogOperations.enrichmentValidationAll(workflowId), 'products'] as const,
    /** One View-products page: the validation run state it reflects (id + status, so a new or
     *  settled run refetches it), its filters (`''` = unset) and 1-based page. */
    enrichmentValidationProducts: (
      workflowId: string,
      validationRunId: string,
      runStatus: string,
      filters: {
        status: string;
        q: string;
        rule: string;
        field: string;
        reenrich: string;
        sort: string;
        page: number;
      },
    ) =>
      [
        ...queryKeys.catalogOperations.enrichmentValidationProductsAll(workflowId),
        validationRunId,
        runStatus,
        filters,
      ] as const,
    /** View-products filter-menu counts under one run state, `status` and search (`''` = none). */
    enrichmentValidationFacets: (
      workflowId: string,
      validationRunId: string,
      runStatus: string,
      status: string,
      q: string,
    ) =>
      [
        ...queryKeys.catalogOperations.enrichmentValidationAll(workflowId),
        'facets',
        validationRunId,
        runStatus,
        status,
        q,
      ] as const,
    batchProducts: (jobId: string, tab: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'products', tab] as const,
    /** One page of a job's pipeline enriched products (`{jobId}/pipeline/
     *  enriched-products`), keyed on the job uuid + page + size. Nested under
     *  `batches`/jobId so the batch-detail invalidation refetches it too. */
    pipelineEnrichedProducts: (jobId: string, page: number, size: number) =>
      [
        ...queryKeys.catalogOperations.all,
        'batches',
        jobId,
        'pipeline-enriched-products',
        page,
        size,
      ] as const,
    productDetail: (jobId: string, productId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', jobId, 'products', productId] as const,
    /** Enrichment payload keyed on productId + the includeFailed flag. */
    productEnrichment: (
      productId: string,
      includeFailed: boolean,
      connectorCode?: string,
      jobId?: string,
    ) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        productId,
        'enrichment',
        includeFailed,
        connectorCode ?? '',
        // Last, so invalidating (productId, includeFailed, connectorCode) still matches by prefix.
        jobId ?? '',
      ] as const,
    /** Every enrichment run for one catalog product, keyed on its external id + connector. */
    productEnrichmentHistory: (externalId: string, connectorCode?: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'external-products',
        externalId,
        'enrichment-history',
        connectorCode ?? '',
      ] as const,
    /** Which locales a run has been translated into (per-locale summary). */
    runTranslations: (workflowId: string) =>
      [...queryKeys.catalogOperations.all, 'translations', workflowId] as const,
    /** One product's content in every stored locale. */
    productTranslations: (workflowId: string, productId: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        productId,
        'translations',
        workflowId,
      ] as const,
    /** On-demand data-score read-back (GET), keyed on workflowId + productId +
     *  connector. Distinct from `productEnrichment`: on-demand scores live under a
     *  dedicated backend scope and never surface on the legacy per-product read. */
    productOnDemandScore: (workflowId: string, productId: string, connectorCode?: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        productId,
        'on-demand-score',
        workflowId,
        connectorCode ?? '',
      ] as const,
    /** Attribute-edit provenance. Keyed on productId + connector, matching the
     *  endpoint's own scoping — edits are recorded per connector. */
    productAttributeEdits: (productId: string, connectorCode?: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        productId,
        'attribute-edits',
        connectorCode ?? '',
      ] as const,
    /** Gap-analysis payload. Keyed on productId alone — the endpoint takes no
     *  params now, resolving the connector and run server-side. */
    productGapAnalysis: (productId: string) =>
      [...queryKeys.catalogOperations.all, 'products', productId, 'gap-analysis'] as const,
    /** Search Impact tab payload (descriptor coverage + change ledger + review).
     *  Per producer, like search descriptors: the same product enriched by two
     *  batches has two results, so `enrichmentJobId` is part of the key. */
    productSearchImpact: (productId: string, enrichmentJobId?: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        productId,
        'search-impact',
        enrichmentJobId ?? null,
      ] as const,
    /** Batch strip on the Search Impact tab. */
    jobSearchImpactSummary: (jobId: string) =>
      [...queryKeys.catalogOperations.all, 'jobs', jobId, 'search-impact-summary'] as const,
    /** Search descriptors + phrase descriptions, which share one endpoint and so
     *  one cache slot. `enrichmentJobId` is part of the key, not just the request:
     *  the payload is per-producer, so two batches that enriched the same product
     *  would otherwise share a slot and serve each other's descriptors. Empty
     *  string = the derive path (server picks the product's latest run). */
    productSearchDescriptors: (productId: string, enrichmentJobId?: string) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        productId,
        'search-descriptors',
        enrichmentJobId ?? '',
      ] as const,
    needsAttention: () => [...queryKeys.catalogOperations.all, 'needs-attention'] as const,
    needsAttentionProducts: () =>
      [...queryKeys.catalogOperations.all, 'needs-attention', 'products'] as const,
    /** Tags & trends for a page of products — keyed on producer kind (job or
     *  experiment) + its id + the page's product ids, so each gets its own cache slot. */
    productTagsTrends: (scope: 'job' | 'experiment', id: string, productIds: string[]) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        'tags-trends',
        scope,
        id,
        productIds,
      ] as const,
    /** Enriched/not-enriched flags for a page of products — keyed on job id +
     *  the page's product id list, so each page gets its own cache slot. */
    productEnrichmentStatus: (jobId: string, productIds: string[]) =>
      [
        ...queryKeys.catalogOperations.all,
        'products',
        'enrichment-status',
        jobId,
        productIds,
      ] as const,
    /** Retryable Temporal failure points, keyed on the workflow id
     *  (`BatchDetail.batchId`), not the job uuid. */
    failurePoints: (workflowId: string) =>
      [...queryKeys.catalogOperations.all, 'batches', workflowId, 'failure-points'] as const,
    /** Scheduled catalog operations. `all` is the invalidation root every
     *  mutation targets — pausing one schedule changes the list rows AND the
     *  server-computed ribbon, so they must never be invalidated apart. */
    schedules: {
      // Spelled out rather than spreading `catalogOperations.all` — a nested
      // group's eagerly-evaluated key can't reference the object it lives in.
      all: ['catalog-operations', 'schedules'] as const,
      /** Paged list rows. The filter object is part of the key so each
       *  permutation gets its own slot; `list` and `ribbon` are deliberately
       *  keyed on the SAME filter object so a filter change refetches both. */
      list: (filters: Record<string, unknown>) =>
        [...queryKeys.catalogOperations.schedules.all, 'list', filters] as const,
      /** Aggregate ribbon over the whole filtered set — same filters as `list`. */
      ribbon: (filters: Record<string, unknown>) =>
        [...queryKeys.catalogOperations.schedules.all, 'ribbon', filters] as const,
      /** Dashboard rollup — takes no params. */
      overview: () => [...queryKeys.catalogOperations.schedules.all, 'overview'] as const,
      /** Cross-schedule run feed, keyed on its filter permutation. */
      runs: (filters: Record<string, unknown>) =>
        [...queryKeys.catalogOperations.schedules.all, 'runs', filters] as const,
      /** Single schedule detail (durable row + live Temporal block). */
      detail: (scheduleId: string) =>
        [...queryKeys.catalogOperations.schedules.all, scheduleId] as const,
      /** Run history for one schedule. */
      scheduleRuns: (scheduleId: string) =>
        [...queryKeys.catalogOperations.schedules.all, scheduleId, 'runs'] as const,
    },
  },
  /** Curation products listing — paginated per curation run. The `filters`
   *  object scopes a separate cache slot per filter permutation so each
   *  unique view gets its own cache entry. */
  curation: {
    all: ['curation'] as const,
    runProducts: (
      curationRefId: string,
      page: number,
      size: number,
      filters: Record<string, unknown> = {},
      search = '',
      sortBy: string | null = null,
      sortDir = 'asc',
    ) =>
      [
        'curation',
        'runs',
        curationRefId,
        'products',
        page,
        size,
        filters,
        search,
        sortBy,
        sortDir,
      ] as const,
    /** Single product detail keyed on the curation feed_product_id. */
    productDetail: (productId: string) =>
      [...queryKeys.curation.all, 'products', productId] as const,
    /** Version timeline for a FEED product. */
    productVersions: (feedProductId: string) =>
      [...queryKeys.curation.all, 'products', feedProductId, 'versions'] as const,
    /** One archived-or-live version of a feed product. The version number is part
     *  of the key so a historical payload can never be served where the live one
     *  belongs — they share a shape, so a collision would be invisible. */
    productVersion: (feedProductId: string, version: number) =>
      [...queryKeys.curation.all, 'products', feedProductId, 'versions', version] as const,
    /** Timeline for an INTERNAL (curated) product — a different entity from the
     *  feed product above, with its own counter, hence its own key prefix. */
    internalProductVersions: (productId: string) =>
      [...queryKeys.curation.all, 'internal-products', productId, 'versions'] as const,
    /** Batch "current" values for a set of products in one curation run — one
     *  request instead of one detail call per product. */
    productsByIds: (curationRefId: string, productIds: readonly string[]) =>
      [...queryKeys.curation.all, 'runs', curationRefId, 'products-by-ids', productIds] as const,
    /** Global needs-attention listing — scoped by subsidiary, optional
     *  connector, pagination, and the filter object so each unique view
     *  gets its own cache slot. Empty string for connector = cross-connector
     *  mode. */
    needsAttention: (
      connectorCode: string,
      page: number,
      size: number,
      filters: Record<string, unknown> = {},
      search = '',
    ) =>
      [
        ...queryKeys.curation.all,
        'needs-attention',
        connectorCode,
        page,
        size,
        filters,
        search,
      ] as const,
    /** Subsidiary-scoped curation stats (drives the catalog-page alert banner). */
    stats: (subsidiaryId: string) => [...queryKeys.curation.all, 'stats', subsidiaryId] as const,
    /** Filter definitions scoped by subsidiary + connector. `connectorCode`
     *  may be empty (overlap set), so it's part of the key for cache slotting. */
    filters: (subsidiaryId: string, connectorCode: string) =>
      [...queryKeys.curation.all, 'filters', subsidiaryId, connectorCode] as const,
    /** Filter definitions discovered from one run's products. */
    runFilters: (subsidiaryId: string, curationRefId: string, connectorCode: string) =>
      [
        ...queryKeys.curation.all,
        'run-filters',
        subsidiaryId,
        curationRefId,
        connectorCode,
      ] as const,
    /** Async by-selection breakdown SUBMIT (③) — keyed on the scope (connector
     *  + sync + filters) so a distinct scope creates a distinct job. The POST is
     *  modelled as a query so it auto-refires when the debounced scope changes. */
    alreadyCuratedBySelectionSubmit: (
      connectorCode: string,
      syncId: string | null,
      filters: Record<string, unknown>,
    ) =>
      [
        ...queryKeys.curation.all,
        'already-curated',
        'by-selection',
        'submit',
        connectorCode,
        syncId,
        filters,
      ] as const,
    /** Async by-selection breakdown POLL (③) — keyed on the job id returned by
     *  the submit call. */
    alreadyCuratedBySelectionStatus: (jobId: string) =>
      [...queryKeys.curation.all, 'already-curated', 'by-selection', 'status', jobId] as const,
    /** Row drill-down (④) — keyed on the scope + bucket so each bucket/scope
     *  caches its own page chain. The keyset `cursor` is threaded via
     *  `pageParam` and deliberately excluded from the key.
     *
     *  `search`/`sortBy`/`sortOrder` ARE part of the key: they change the server's
     *  ordering, and cursors are only valid for the ordering that minted them, so a
     *  shared key would splice pages from different orderings into one list. A new
     *  key restarts the chain from page 1, which is exactly what the contract wants. */
    alreadyCuratedRows: (
      connectorCode: string,
      bucket: string,
      syncId: string | null,
      filters: Record<string, unknown>,
      search: string | null,
      sortBy: string | null,
      sortOrder: string | null,
    ) =>
      [
        ...queryKeys.curation.all,
        'already-curated',
        'rows',
        connectorCode,
        bucket,
        syncId,
        filters,
        search,
        sortBy,
        sortOrder,
      ] as const,
    /** Run-scoped already-curated reconciliation (②) — keyed on the curation
     *  run plus the optional explicit id list and pagination so each view
     *  caches independently. `sourceProductIds` null = self-serve the run. */
    runAlreadyCurated: (
      curationRefId: string,
      sourceProductIds: readonly string[] | null,
      page: number,
      size: number,
    ) =>
      [
        ...queryKeys.curation.all,
        'runs',
        curationRefId,
        'already-curated',
        sourceProductIds,
        page,
        size,
      ] as const,
  },
  /** Trial workflow — import-status polling. */
  trial: {
    all: ['trial'] as const,
    importStatus: (workflowId: string) => ['trial', 'import-status', workflowId] as const,
  },
  /** Trial enrichment flow (v2) — content-scoring and future stage polling keys. */
  trialEnrichment: {
    all: ['trial-enrichment'] as const,
    /** Full trial detail — fetched once on wizard load for cold-reload step resolution. */
    detail: (id: string) => ['trial-enrichment', 'detail', id] as const,
    /** Polled per trial id until Stage 1 reaches a terminal status. */
    contentScoringStatus: (id: string) =>
      ['trial-enrichment', 'content-scoring-status', id] as const,
    /** Polled per trial id until Stage 2 sample-enrichment reaches a terminal status. */
    sampleEnrichmentStatus: (id: string) =>
      ['trial-enrichment', 'sample-enrichment-status', id] as const,
    /** Fetched once after Stage 2 completes — powers the Content Quality Score step. */
    qualityScore: (id: string) => ['trial-enrichment', 'quality-score', id] as const,
    /** Paginated preview cards for the Preview Products step; keyed by page + size. */
    sampleEnrichmentPreview: (id: string, page: number, size: number) =>
      ['trial-enrichment', 'sample-enrichment-preview', id, page, size] as const,
    /** Polled after full-catalog trigger until AWAITING_APPROVAL or FAILED. */
    fullCatalogStatus: (id: string) => ['trial-enrichment', 'full-catalog-status', id] as const,
    /** Fetched once after Stage 3 reaches AWAITING_APPROVAL — powers the Content Quality Score step. */
    fullCatalogQualityScore: (id: string) =>
      ['trial-enrichment', 'full-catalog-quality-score', id] as const,
    /** Paginated enriched product preview; keyed by page + size. */
    fullCatalogEnrichedProducts: (id: string, page: number, size: number) =>
      ['trial-enrichment', 'full-catalog-enriched-products', id, page, size] as const,
  },
  /** Feed Audit (super-admin) — list, job detail, and enrichment stage polling. */
  feedAudit: {
    all: ['feed-audit'] as const,
    /** Past feed-audit runs for the active tenant. */
    list: () => ['feed-audit', 'list'] as const,
    /** Current-sync catalog products for scoping/selection during creation. */
    products: () => ['feed-audit', 'products'] as const,
    /** Polled workflow progress snapshot (stage statuses + open gate). */
    progress: (workflowId: string) => ['feed-audit', 'progress', workflowId] as const,
    /** Full feed-audit job detail — fetched once on wizard load for step resolution. */
    detail: (id: string) => ['feed-audit', 'detail', id] as const,
    /** Paginated enriched product preview; keyed by page + size. */
    fullCatalogEnrichedProducts: (id: string, page: number, size: number) =>
      ['feed-audit', 'full-catalog-enriched-products', id, page, size] as const,
    /** Every page/size of one job's preview — the prefix an attribute edit invalidates,
     *  since the preview step and the export menu each hold their own page size. */
    fullCatalogEnrichedProductsAll: (id: string) =>
      ['feed-audit', 'full-catalog-enriched-products', id] as const,
    /** Meta rules the Meta audit computes its verdicts from (static per agent deploy). */
    metaSpec: () => ['feed-audit', 'meta-spec'] as const,
    /** Each product's merchant row (Meta field → value), the Meta audit's "before" side. */
    metaSources: (curationRefId: string, productIds: string[]) =>
      ['feed-audit', 'meta-sources', curationRefId, productIds] as const,
    /** The file behind an audit's upload (`importRefId`) — metadata + revisions. */
    sourceFile: (uploadId: string) => ['feed-audit', 'source-file', uploadId] as const,
    /** That file's download history, one page. */
    sourceFileDownloads: (uploadId: string, page: number) =>
      ['feed-audit', 'source-file', uploadId, 'downloads', page] as const,
  },
  /** GCS-backed file upload flow — blob preview and upload-row status polling. */
  uploads: {
    all: ['uploads'] as const,
    /** Stateless blob preview keyed by blob path — re-fetched whenever gcsBlobName changes. */
    /** Stateless blob preview. `maxRows` is part of the key — a 5-row and a 100-row read of
     *  the same blob are different payloads and must not share a cache entry. */
    blobPreview: (gcsBlobName: string, subsidiaryId?: string, maxRows?: number) =>
      ['uploads', 'blob-preview', gcsBlobName, subsidiaryId ?? null, maxRows ?? null] as const,
    /** Upload-scoped row preview keyed by id + row limit (+ subsidiary scope). */
    preview: (uploadId: string, limit: number, subsidiaryId?: string) =>
      ['uploads', 'preview', uploadId, limit, subsidiaryId ?? null] as const,
    /** One upload row by id (+ subsidiary scope). Distinct from `deliveryStatus`, which is the
     *  live workflow view — this is the persisted row, and the fallback source of its group id. */
    detail: (uploadId: string, subsidiaryId?: string) =>
      ['uploads', 'detail', uploadId, subsidiaryId ?? null] as const,
    /** Column-mapping preview keyed by connector code + blob path (+ subsidiary scope). */
    previewMapping: (connectorCode: string, blobName: string, subsidiaryId?: string) =>
      ['uploads', 'preview-mapping', connectorCode, blobName, subsidiaryId ?? null] as const,
    /** Aggregated catalogue quality + eligibility summary keyed by syncId. */
    summary: (syncId: string) => ['uploads', 'summary', syncId] as const,
    /** Upload row status — polled until the status reaches a terminal state. */
    status: (id: string) => ['uploads', 'status', id] as const,
    /** Combined live + persisted delivery status — polled through the approval + delivery stages.
     *  `subsidiaryId` partitions the cache when a page-local subsidiary scope is active. */
    deliveryStatus: (id: string, subsidiaryId?: string) =>
      ['uploads', 'delivery-status', id, subsidiaryId ?? null] as const,
    /** Blobs in the connector-scoped upload lane (Entry B), keyed by connector code + subsidiary. */
    browse: (connectorCode: string, subsidiaryId?: string) =>
      ['uploads', 'browse', connectorCode, subsidiaryId ?? null] as const,
    /** Staged-feed inbox (`POST /uploads/list`) keyed by filter body + page. */
    list: (filters: Record<string, unknown>, page: number, size: number) =>
      ['uploads', 'list', filters, page, size] as const,
    /** Column names + inferred datatypes of a staged feed. */
    headers: (uploadId: string) => ['uploads', 'headers', uploadId] as const,
    /** Upload-scoped filter catalog (which fields of a staged feed are filterable). */
    filterCatalog: (uploadId: string) => ['uploads', 'filter-catalog', uploadId] as const,
    /** Staged-feed quality KPIs (`GET|POST /uploads/{id}/summary`), filter-aware. */
    qualitySummary: (uploadId: string, filters: Record<string, unknown> | null) =>
      ['uploads', 'quality-summary', uploadId, filters] as const,
  },
  /** Upload-groups — the group-scoped filter catalog + preview reused by both the
   *  single-file (via N=1 auto-wrap) and multi-file delivery flows. `preview` is
   *  keyed by the applied-filter set + page/size so each filter caches independently. */
  uploadGroups: {
    all: ['upload-groups'] as const,
    /** Paginated groups list, keyed by page/size/status/search + subsidiary so filtered/scoped pages cache apart. */
    list: (page: number, size: number, status?: string, subsidiaryId?: string, search?: string) =>
      [
        ...queryKeys.uploadGroups.all,
        'list',
        page,
        size,
        status ?? null,
        subsidiaryId ?? null,
        search ?? null,
      ] as const,
    /** Full group detail — polled through INGESTING / delivery transitions. */
    detail: (groupId: string, subsidiaryId?: string) =>
      [...queryKeys.uploadGroups.all, 'detail', groupId, subsidiaryId ?? null] as const,
    filters: (groupId: string, subsidiaryId?: string) =>
      [...queryKeys.uploadGroups.all, 'filters', groupId, subsidiaryId ?? null] as const,
    preview: (
      groupId: string,
      filters: Record<string, unknown> | null,
      page: number,
      size: number,
      subsidiaryId?: string,
    ) =>
      [
        ...queryKeys.uploadGroups.all,
        'preview',
        groupId,
        filters,
        page,
        size,
        subsidiaryId ?? null,
      ] as const,
  },
  contentEnrichment: {
    all: ['content-enrichment'] as const,
    /** Aggregated catalog summary — keyed by connection + filter set + the
     *  experiment's catalog snapshot so different filter / syncId combinations
     *  cache independently. `syncId` is `null` for legacy rows that pre-date
     *  snapshot pinning. */
    summary: (
      connectionId: string,
      filters: Record<string, string | string[]>,
      syncId: string | null,
    ) => [...queryKeys.contentEnrichment.all, 'summary', connectionId, filters, syncId] as const,
    sampleResults: (jobId: string) =>
      [...queryKeys.contentEnrichment.all, 'sample-results', jobId] as const,
    fullStatus: (jobId: string) =>
      [...queryKeys.contentEnrichment.all, 'full-status', jobId] as const,
    comparison: (connectionId: string, jobId: string) =>
      [...queryKeys.contentEnrichment.all, 'comparison', connectionId, jobId] as const,
    /** Retryable Temporal failure points for a failed pipeline run, keyed on the
     *  root workflow id the control rail acts on. */
    failurePoints: (workflowId: string) =>
      [...queryKeys.contentEnrichment.all, 'failure-points', workflowId] as const,
    /** Restart-gating snapshot (stage + remaining products) for the pipeline run,
     *  keyed on the root workflow id. */
    runProgress: (workflowId: string) =>
      [...queryKeys.contentEnrichment.all, 'run-progress', workflowId] as const,
    /** Live health (paused / state) for the pipeline run, keyed on the root
     *  workflow id — drives the paused presentation on the Full Catalog step. */
    runHealth: (workflowId: string) =>
      [...queryKeys.contentEnrichment.all, 'run-health', workflowId] as const,
  },
  configuration: {
    all: ['configuration'] as const,
    /** Platform name → UUID lookup, fetched once and cached forever. */
    platforms: () => [...queryKeys.configuration.all, 'platforms'] as const,
    /** Parameters scoped by platform UUID + optional server-side filters. */
    parameters: (params: Record<string, unknown>) =>
      [...queryKeys.configuration.all, 'parameters', params] as const,
    /** Category list with counts, scoped by platform UUID. */
    parameterCategories: (platformId: string) =>
      [...queryKeys.configuration.all, 'parameter-categories', platformId] as const,
    /** Per-parameter history — backed by `/config-audit-trail`, used in both
     *  global and subsidiary scope (see useParameterHistory.ts). */
    parameterHistory: (id: string) =>
      [...queryKeys.configuration.all, 'parameters', id, 'history'] as const,
    /** Audit-trail listing scoped by filters object. */
    auditTrail: (params: Record<string, unknown>) =>
      [...queryKeys.configuration.all, 'audit-trail', params] as const,
    /** Filter options { parameters, users } scoped by platform UUID. */
    auditTrailFilters: (platformId: string) =>
      [...queryKeys.configuration.all, 'audit-trail', 'filters', platformId] as const,
    /** Subsidiary-parameters scoped by platform UUID + filters — separate
     *  cache slot from the global `parameters` key so switching between
     *  global/subsidiary mode never serves a stale cross-mode cache hit. */
    subsidiaryParameters: (params: Record<string, unknown>) =>
      [...queryKeys.configuration.all, 'subsidiary-parameters', params] as const,
    /** Subsidiary-scoped audit-trail listing, scoped by filters object. */
    subsidiaryAuditTrail: (params: Record<string, unknown>) =>
      [...queryKeys.configuration.all, 'subsidiary-audit-trail', params] as const,
    /** Treatment Configuration — current arm count + arm/production treatments
     *  for a platform. Keyed on the platform UUID so platform switches refetch. */
    treatmentConfig: (platformId: string) =>
      [...queryKeys.configuration.all, 'treatment-config', platformId] as const,
    /** Treatment catalogue — platform-agnostic; powers every select on the page. */
    treatments: () => [...queryKeys.configuration.all, 'treatments'] as const,
    /** Subsidiary-scoped Treatment Configuration — separate cache slot from
     *  the global `treatmentConfig` key, same rationale as `subsidiaryParameters`. */
    subsidiaryTreatmentConfig: (platformId: string) =>
      [...queryKeys.configuration.all, 'subsidiary-treatment-config', platformId] as const,
    /** Dashboard list with platform's enabled state. Keyed on platform UUID. */
    dashboards: (platformId: string) =>
      [...queryKeys.configuration.all, 'dashboards', platformId] as const,
    /** Subsidiary-scoped dashboard list — separate cache slot from the global
     *  `dashboards` key, same rationale as `subsidiaryParameters`. */
    subsidiaryDashboards: (platformId: string) =>
      [...queryKeys.configuration.all, 'subsidiary-dashboards', platformId] as const,
    /** One page of a registered config table (`POST /tables/rows`). Keyed on the
     *  table name *and* the full filter + pagination object, so every
     *  filter/page combination is its own cache slot and two tables never
     *  collide (server-paginated: 127k rows on `taxonomy_entries`, so a page is
     *  never derivable from another page's cache). */
    tableRows: (tableName: string, params: Record<string, unknown>) =>
      [...queryKeys.configuration.all, 'table-rows', tableName, params] as const,
    /** Column metadata + filter facet values for one registered table
     *  (`POST /tables/columns`). Keyed on the table name — the endpoint is
     *  generic, so a second table must not share this cache slot. */
    tableColumns: (tableName: string) =>
      [...queryKeys.configuration.all, 'table-columns', tableName] as const,
    /** Ask AI's field catalogue + starter prompts for one table
     *  (`POST /tables/ai-filter/suggestions`). Its own slot rather than a
     *  `tableColumns` variant — a different endpoint returning a different
     *  payload. The per-turn `/tables/ai-filter` call is a mutation, so it has no
     *  key here: it is never cached, and replaying a conversation is the caller's
     *  job. */
    tableAiSuggestions: (tableName: string) =>
      [...queryKeys.configuration.all, 'table-ai-suggestions', tableName] as const,
    /** Every locale row for one `gpc_id` — the cross-locale lookup the GPC detail
     *  panel opens with. Its own key rather than a `tableRows` variant so it can
     *  never collide with the grid's cache slot for the same table. */
    gpcCategoryLocales: (gpcId: number) =>
      [...queryKeys.configuration.all, 'gpc-category-locales', gpcId] as const,
    /** Every locale row for one `fpc_id` — the cross-locale lookup the FPC detail
     *  panel opens with. Its own key rather than a `gpcCategoryLocales` variant: the
     *  two id spaces are unrelated, so `68` in Meta's taxonomy and `68` in Google's
     *  must not share a cache slot. */
    fpcCategoryLocales: (fpcId: number) =>
      [...queryKeys.configuration.all, 'fpc-category-locales', fpcId] as const,
    /** Prompt catalog (data-scoring agent, via `agentsClient`) — the namespace listing
     *  with per-namespace counts. Unparameterised: one catalog per deployment. */
    promptNamespaces: () => [...queryKeys.configuration.all, 'prompt-namespaces'] as const,
    /** One namespace's prompts + Jinja2 templates. Keyed on the namespace, which is the
     *  route's only path parameter and the catalog's actual unit of identity. */
    promptNamespace: (namespace: string) =>
      [...queryKeys.configuration.all, 'prompt-namespace', namespace] as const,
    /** Every category-level prompt flattened across namespaces — what the rail
     *  enumerates. Separate slot from `promptNamespaces`: same catalog, different grain. */
    promptCategories: () => [...queryKeys.configuration.all, 'prompt-categories'] as const,
    /** One prompt's full text. Keyed on (namespace, key) because the same key exists in
     *  more than one namespace (`apparel-and-accessories` under both google_agent and a
     *  data_scoring namespace), so the namespace is part of the identity, not context. */
    promptContent: (namespace: string, key: string) =>
      [...queryKeys.configuration.all, 'prompt-content', namespace, key] as const,
  },
  /** Catalog cost-approval gate settings — one global row (agentic-config). */
  /** Platform ▸ Schedules (cross-subsidiary, platform admin). Filter objects carry the window
   *  spec (preset or custom days), never resolved instants, so a key stays stable across renders. */
  platformSchedules: {
    all: ['platform-schedules'] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.platformSchedules.all, 'list', params] as const,
    summary: (params: Record<string, unknown>) =>
      [...queryKeys.platformSchedules.all, 'summary', params] as const,
    runs: (params: Record<string, unknown>) =>
      [...queryKeys.platformSchedules.all, 'runs', params] as const,
    upcoming: (params: Record<string, unknown>) =>
      [...queryKeys.platformSchedules.all, 'upcoming', params] as const,
  },
  /** Platform ▸ Coverage (cross-subsidiary, platform admin), keyed on the full filter object. */
  platformCoverage: {
    all: ['platform-coverage'] as const,
    rows: (params: Record<string, unknown>) =>
      [...queryKeys.platformCoverage.all, 'rows', params] as const,
    stats: (params: Record<string, unknown>) =>
      [...queryKeys.platformCoverage.all, 'stats', params] as const,
    insights: (params: Record<string, unknown>) =>
      [...queryKeys.platformCoverage.all, 'insights', params] as const,
  },
  /** Failed batch review (cross-subsidiary, super-admin). Filters objects carry the window preset. */
  batchReview: {
    all: ['batch-review'] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.batchReview.all, 'list', params] as const,
    summary: (params: Record<string, unknown>) =>
      [...queryKeys.batchReview.all, 'summary', params] as const,
    detail: (jobId: string) => [...queryKeys.batchReview.all, 'detail', jobId] as const,
    failurePoints: (jobId: string) =>
      [...queryKeys.batchReview.all, 'failure-points', jobId] as const,
    subsidiaries: () => [...queryKeys.batchReview.all, 'subsidiaries'] as const,
  },
  costApprovalSettings: {
    all: ['cost-approval-settings'] as const,
    detail: () => [...queryKeys.costApprovalSettings.all, 'detail'] as const,
    /** Paginated change history, keyed on the pagination object. */
    history: (params: Record<string, unknown>) =>
      [...queryKeys.costApprovalSettings.all, 'history', params] as const,
  },
  /** The signed-in user's own preferences (agentic-config), keyed by user so a later sign-in
   *  on the same tab never reads the previous user's. */
  myPreferences: {
    all: ['my-preferences'] as const,
    detail: (userId: string) => [...queryKeys.myPreferences.all, userId] as const,
  },
  /** Catalog cost estimates (workflow-webapp), one entry per run workflow id. */
  costEstimates: {
    all: ['cost-estimates'] as const,
    /** Namespaced: a bare `[all, workflowId]` would equal `approverStatus()` / prefix `list` /
     *  `history` for an id spelled like those segments. */
    byWorkflow: (workflowId: string) =>
      [...queryKeys.costEstimates.all, 'by-workflow', workflowId] as const,
    rates: (subsidiaryId: string, connectorCode: string, contentFields: number) =>
      [
        ...queryKeys.costEstimates.all,
        'rates',
        subsidiaryId,
        connectorCode,
        contentFields,
      ] as const,
    list: (status: string, tier: string) =>
      [...queryKeys.costEstimates.all, 'list', status, tier] as const,
    /** Per signed-in user: a cached answer must never outlive the user it was asked for. Without
     *  `userId` it is the prefix every user's entry shares (logout clears it). */
    approverStatus: (userId?: string) =>
      userId === undefined
        ? ([...queryKeys.costEstimates.all, 'approver-status'] as const)
        : ([...queryKeys.costEstimates.all, 'approver-status', userId] as const),
    /** Decided-estimates history, keyed on its filters and page. */
    history: (params: Record<string, unknown>) =>
      [...queryKeys.costEstimates.all, 'history', params] as const,
    actual: (workflowId: string) => [...queryKeys.costEstimates.all, 'actual', workflowId] as const,
  },
  /** Run time estimates (workflow-service `run-estimates/time`). */
  runEstimates: {
    all: ['run-estimates'] as const,
    time: (subsidiaryId: string, connectorCode: string, products: number, scoringPhases: number) =>
      [
        ...queryKeys.runEstimates.all,
        'time',
        subsidiaryId,
        connectorCode,
        products,
        scoringPhases,
      ] as const,
  },
  /** Enrichment Limits — global numeric limits (agentic-config). */
  enrichmentLimits: {
    all: ['enrichment-limits'] as const,
    /** The full limits list (no server-side filters — small, fixed set). */
    list: () => [...queryKeys.enrichmentLimits.all, 'list'] as const,
    /** Paginated audit trail, keyed on the filter + pagination object. */
    auditTrail: (params: Record<string, unknown>) =>
      [...queryKeys.enrichmentLimits.all, 'audit-trail', params] as const,
  },
  navigation: {
    all: ['navigation'] as const,
    menu: (subsidiaryId: string, userId: string, isImpersonating: boolean) =>
      ['navigation', subsidiaryId, userId, isImpersonating] as const,
  },
  /** Service Auth — OAuth2 client_credentials principals + discovery docs. */
  serviceAuth: {
    all: ['service-auth'] as const,
    users: () => ['service-auth', 'users'] as const,
    userById: (id: string) => ['service-auth', 'users', id] as const,
    jwks: () => ['service-auth', 'jwks'] as const,
    oidcDiscovery: () => ['service-auth', 'oidc-discovery'] as const,
    audiences: () => ['service-auth', 'audiences'] as const,
    scopes: () => ['service-auth', 'scopes'] as const,
  },
  /** Audit Logs — admin list, detail, by-request-id. */
  auditLogs: {
    all: ['audit-logs'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.auditLogs.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.auditLogs.all, 'detail', id] as const,
    byRequestId: (requestId: string) =>
      [...queryKeys.auditLogs.all, 'by-request-id', requestId] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    channelConfigs: () => [...queryKeys.notifications.all, 'channel-configs'] as const,
    configurations: (audience?: 'customer' | 'internal') =>
      [...queryKeys.notifications.all, 'configurations', { audience: audience ?? 'all' }] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
    preferenceHistory: (preferenceId: string) =>
      [...queryKeys.notifications.all, 'preferences', preferenceId, 'history'] as const,
    /** User-facing inbox of received notifications. Paged variants key off
     *  `{ page, size }`; the no-arg base is what the SSE stream provider and
     *  mark-read mutations invalidate — prefix-matching every paged variant. */
    inbox: (params?: { page: number; size: number }) =>
      [...queryKeys.notifications.all, 'inbox', ...(params ? [params] : [])] as const,
    /** Live unread count — drives the bell badge; updated by SSE stream events. */
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
    /** All notifications list — full-page view. */
    allNotifications: () => [...queryKeys.notifications.all, 'all-notifications'] as const,
  },
  /**
   * Scheduled enrichment — Meta Ads (and future connectors).
   * Keyed by workflowId so each trigger's status / quality-score cache slot
   * stays isolated and can be invalidated independently.
   */
  notEnriched: {
    all: ['not-enriched'] as const,
    summary: (subsidiaryId: string, connectorCode: string, scope?: unknown) =>
      scope === undefined
        ? (['not-enriched', 'summary', subsidiaryId, connectorCode] as const)
        : (['not-enriched', 'summary', subsidiaryId, connectorCode, scope] as const),
    products: (subsidiaryId: string, connectorCode: string, bucket?: string) =>
      ['not-enriched', 'products', subsidiaryId, connectorCode, bucket ?? 'all'] as const,
  },
  scheduledEnrichment: {
    all: ['scheduled-enrichment'] as const,
    /** Poll status of any scheduled trigger workflow. */
    triggerStatus: (wfId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'status', wfId] as const,
    /** Aggregate quality scores once content-scoring COMPLETED. */
    contentScoringQualityScore: (wfId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'content-scoring-quality-score', wfId] as const,
    /** Before/After/Lift quality scores once sample-enrichment COMPLETED. */
    sampleEnrichmentQualityScore: (wfId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'sample-enrichment-quality-score', wfId] as const,
    /** Paginated Before/After product cards from a completed sample-enrichment run. */
    sampleEnrichmentPreview: (wfId: string, page: number, size: number) =>
      [
        ...queryKeys.scheduledEnrichment.all,
        'sample-enrichment-preview',
        wfId,
        page,
        size,
      ] as const,
    /** Paginated enriched products from a completed pipeline run. */
    pipelineEnrichedProducts: (wfId: string, page: number, size: number) =>
      [...queryKeys.scheduledEnrichment.all, 'enriched-products', wfId, page, size] as const,
    /** All pages for a single workflow — used by error boundary reset. */
    pipelineEnrichedProductsAll: (wfId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'enriched-products', wfId] as const,
    /** Every server page of the run folded into one entry, for the listings that
     *  paginate client-side. Under `...All` so the prefix reset still covers it. */
    pipelineEnrichedProductsComplete: (wfId: string) =>
      [...queryKeys.scheduledEnrichment.pipelineEnrichedProductsAll(wfId), 'complete'] as const,
    /** Before/After/Lift quality scores from the completed Meta pipeline run. */
    pipelineQualityScore: (wfId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'pipeline-quality-score', wfId] as const,
    /** Full job-status snapshot including all three sub-workflow blocks. */
    jobStatus: (jobId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'job-status', jobId] as const,
    /** Stored (editable) run config of one scheduled job. */
    jobRunConfig: (jobId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'job-run-config', jobId] as const,
    /** Run-config change history of one scheduled job. */
    jobRunConfigHistory: (jobId: string) =>
      [...queryKeys.scheduledEnrichment.all, 'job-run-config-history', jobId] as const,
    /** Per-product Meta scorer drill-down — keyed by wfId + productId + stage. */
    metaProductScoreDetails: (wfId: string, productId: string, stage: string) =>
      [
        ...queryKeys.scheduledEnrichment.all,
        'meta-product-score-details',
        wfId,
        productId,
        stage,
      ] as const,
  },
  /**
   * Coverage Report — cross-subsidiary enrichment coverage.
   * Scoped per unique param combination so tab / page / search changes each
   * get their own cache slot without invalidating unrelated views.
   */
  coverageReport: {
    all: ['coverage-report'] as const,
    /** Paginated rows — keyed on the full param set (tab, filters, sort, page). */
    report: (params: Record<string, unknown>) =>
      [...queryKeys.coverageReport.all, 'report', params] as const,
    /** Header stats — keyed on the filter set only (no pagination / sort). */
    stats: (params: Record<string, unknown>) =>
      [...queryKeys.coverageReport.all, 'stats', params] as const,
  },
  /**
   * Coverage settings — the subsidiary-wide "Coverage tracking" switch. The server scopes
   * it by `X-Subsidiary-ID`, so the cache is keyed by the subsidiary too: a tenant switch
   * must never show the previous subsidiary's switch state.
   */
  coverageSettings: {
    all: ['coverage-settings'] as const,
    detail: (subsidiaryId: string | undefined) =>
      [...queryKeys.coverageSettings.all, subsidiaryId ?? 'none'] as const,
    /** Every subsidiary's switch (Platform ▸ Coverage). */
    list: () => [...queryKeys.coverageSettings.all, 'list'] as const,
  },
  /** Feature Flags — admin search/detail/evaluate + runtime `me` resolution. */
  featureFlags: {
    all: ['feature-flags'] as const,
    me: () => [...queryKeys.featureFlags.all, 'me'] as const,
    search: (filters: Record<string, unknown>, page: number, size: number) =>
      [...queryKeys.featureFlags.all, 'search', filters, page, size] as const,
    detail: (id: string) => [...queryKeys.featureFlags.all, 'detail', id] as const,
    evaluate: (id: string, subsidiaryId: string) =>
      [...queryKeys.featureFlags.all, 'evaluate', id, subsidiaryId] as const,
  },
  /** File transformers (agentic-integrations). Every write invalidates `all`. */
  transformers: {
    all: ['transformers'] as const,
    list: (params: unknown) => [...queryKeys.transformers.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.transformers.all, 'detail', id] as const,
    version: (id: string, version: number) =>
      [...queryKeys.transformers.all, 'version', id, version] as const,
    stepTypes: () => [...queryKeys.transformers.all, 'step-types'] as const,
    preview: (request: unknown) => [...queryKeys.transformers.all, 'preview', request] as const,
    resolve: (
      subsidiaryId: string,
      connector: string,
      connectionId: string | null,
      transformerId: string | null = null,
    ) =>
      [
        ...queryKeys.transformers.all,
        'resolve',
        subsidiaryId,
        connector,
        connectionId,
        transformerId,
      ] as const,
    assignments: (params: unknown) =>
      [...queryKeys.transformers.all, 'assignments', params] as const,
  },
  adminCatalog: {
    all: ['admin-catalog'] as const,
    /** Full product list — no filter params; catalog is small and always fetched whole. */
    products: () => [...queryKeys.adminCatalog.all, 'products'] as const,
  },
  /**
   * Enriched-product CSV exports. `list` is scoped by its optional filters so
   * per-source-run and per-status views cache independently; `detail` keys on
   * the exportId (used for one-shot status reads / SSE fallback polls).
   */
  exports: {
    all: ['exports'] as const,
    list: (sourceWorkflowId?: string, status?: string, createdFrom?: string, createdTo?: string) =>
      [
        ...queryKeys.exports.all,
        'list',
        sourceWorkflowId ?? null,
        status ?? null,
        createdFrom ?? null,
        createdTo ?? null,
      ] as const,
    detail: (exportId: string) => [...queryKeys.exports.all, 'detail', exportId] as const,
    /** An export's configuration snapshot — immutable once written. */
    config: (exportId: string) => [...queryKeys.exports.all, 'config', exportId] as const,
    /** Static templates config — one entry, cached for the session (`staleTime: Infinity`). */
    templates: () => [...queryKeys.exports.all, 'templates'] as const,
    /**
     * Template preview, keyed by everything that changes the rendered cells:
     * the run, the chosen template, the column selection and the filters.
     * `format` is deliberately absent — the preview returns cells, not a
     * delimited line, so the server ignores it.
     */
    templatePreview: (
      sourceWorkflowId: string,
      template: string | null,
      fields: string[] | null,
      filters: unknown,
      rows: number,
    ) =>
      [
        ...queryKeys.exports.all,
        'template-preview',
        sourceWorkflowId,
        template ?? null,
        fields ?? null,
        filters ?? null,
        rows,
      ] as const,
    /**
     * One page of the full-run browse view. Keyed on the cursor and search as
     * well as the render inputs, so paging back to a page already seen is a
     * cache hit rather than a refetch — the enrichment rows are immutable once
     * a run has completed.
     */
    templateBrowse: (
      sourceWorkflowId: string,
      template: string | null,
      fields: string[] | null,
      filters: unknown,
      pageSize: number,
      cursor: string | null,
      search: string | null,
      columnFilters: unknown,
    ) =>
      [
        ...queryKeys.exports.all,
        'template-browse',
        sourceWorkflowId,
        template ?? null,
        fields ?? null,
        filters ?? null,
        pageSize,
        cursor ?? null,
        search ?? null,
        columnFilters ?? null,
      ] as const,
    /** The unified Catalog exports list (sort/cursor/archived/status/q) — its own key so the
     *  richer browse read never collides with `list`'s older, narrower cache entries (still used
     *  by the history sheet and the 409-adopt lookup). */
    /** `object` (not `Record<string, unknown>`) so a named params interface — not just a fresh
     *  literal — is assignable without every caller spreading/casting it first. */
    browseList: (params: object) => [...queryKeys.exports.all, 'browse-list', params] as const,
    /** Exact KPI/tab counts (`GET /v1/exports/counts`) — scoped by the same `archived`/`q`
     *  filters the list itself uses. */
    counts: (params: object) => [...queryKeys.exports.all, 'counts', params] as const,
  },
  /**
   * Export profiles (agentic-integrations, in progress). One `export-profiles` root so a
   * mutation (create/update/pause/resume/delete) can invalidate every list + detail in one call.
   */
  exportProfiles: {
    all: ['export-profiles'] as const,
    list: (params: object) => [...queryKeys.exportProfiles.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.exportProfiles.all, 'detail', id] as const,
    /** A profile's own runs — keyset (`cursor`/`limit`), no status/date filter at this endpoint. */
    runs: (id: string, params: object) =>
      [...queryKeys.exportProfiles.all, id, 'runs', params] as const,
  },
  /** Cross-profile delivery runs list + detail. Separate root from `exportProfiles` — a run
   *  mutation (approve/mark-delivered/cancel/archive) invalidates this tree, not the profiles one,
   *  though the exports-list page reads both. */
  exportProfileRuns: {
    all: ['export-profile-runs'] as const,
    list: (params: object) => [...queryKeys.exportProfileRuns.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.exportProfileRuns.all, 'detail', id] as const,
    /** Exact KPI/tab counts (`GET /export-profile-runs/counts`) — scoped by the same
     *  `archived` filter the list itself uses. */
    counts: (params: object) => [...queryKeys.exportProfileRuns.all, 'counts', params] as const,
  },
  /** Export schedules (the WHEN of an export profile). A schedule mutation also invalidates
   *  `exportProfiles` (the profile embeds `schedules` + `nextRunAt`) and `exportProfileRuns`
   *  (Run now starts a run). */
  exportSchedules: {
    all: ['export-schedules'] as const,
    byProfile: (profileId: string) =>
      [...queryKeys.exportSchedules.all, 'profile', profileId] as const,
    detail: (id: string) => [...queryKeys.exportSchedules.all, 'detail', id] as const,
    /** Keyset firing history — cursors are NOT keyed (infinite pages accumulate here). */
    firings: (id: string, params: object) =>
      [...queryKeys.exportSchedules.all, 'firings', id, params] as const,
    /** Next run times for a draft cadence — keyed on the whole `{cadence, timezone}` body. */
    preview: (body: unknown) => [...queryKeys.exportSchedules.all, 'preview', body] as const,
    /** Tenant-wide list (`profileId` / `status` folded in). */
    list: (params: object) => [...queryKeys.exportSchedules.all, 'list', params] as const,
    /** The Schedules view's overview strip. */
    overview: () => [...queryKeys.exportSchedules.all, 'overview'] as const,
    /** Tenant-wide keyset firing feed — filters keyed, cursors not (pages accumulate here). */
    firingFeed: (params: object) =>
      [...queryKeys.exportSchedules.all, 'firing-feed', params] as const,
  },
  /**
   * Full-catalog export reads. Summary and preview key on the whole request
   * body: every catalog and filter field changes the numbers, and the server
   * caches the same way (per selection fingerprint + filters).
   */
  catalogExports: {
    all: ['catalog-exports'] as const,
    batches: (connector: string, preset: string, includeArchived: boolean) =>
      [...queryKeys.catalogExports.all, 'batches', connector, preset, includeArchived] as const,
    summary: (request: unknown) => [...queryKeys.catalogExports.all, 'summary', request] as const,
    preview: (request: unknown, rows: number) =>
      [...queryKeys.catalogExports.all, 'preview', request, rows] as const,
    /** One browse page — keyed on the whole body, cursor and search included. */
    browse: (request: unknown) => [...queryKeys.catalogExports.all, 'browse', request] as const,
    facets: (request: unknown) => [...queryKeys.catalogExports.all, 'facets', request] as const,
    versions: (request: unknown) => [...queryKeys.catalogExports.all, 'versions', request] as const,
    /** The subsidiary's export history, for the time estimate's throughput. */
    throughput: () => [...queryKeys.catalogExports.all, 'throughput'] as const,
    /** Dry-run row count + size for the current scope. Keyed on the scoped request so every
     *  "what will this export ship" surface (`useCatalogScopedExport`'s callers) shares ONE
     *  TanStack cache entry instead of firing its own estimate. */
    estimate: (request: unknown) => [...queryKeys.catalogExports.all, 'estimate', request] as const,
  },
  /**
   * Workflow execution-tracking (super-admin console). One `runId`-scoped
   * subtree per run so a control action can invalidate everything under a run
   * at once. Filter/pagination params are folded into the key so each
   * permutation caches independently (matches the catalog-ops convention).
   */
  workflowRuns: {
    all: ['workflow-runs'] as const,
    /** Dashboard list, scoped per flowType+status filter permutation. */
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.workflowRuns.all, 'list', filters] as const,
    /** Run detail — reconciled summary + stage checklist. */
    detail: (runId: string) => [...queryKeys.workflowRuns.all, runId, 'detail'] as const,
    /** The run detail's validation-action gates (`invalidItemsRetry` + `repairAction`) only — its
     *  own key, since it caches a projection rather than the console's full `RunDetailDto`. */
    validationActions: (runId: string) =>
      [...queryKeys.workflowRuns.all, runId, 'validation-actions'] as const,
    /** Live "N of M" counter for the current stage. */
    liveProgress: (runId: string) =>
      [...queryKeys.workflowRuns.all, runId, 'live-progress'] as const,
    /** Stage drill-down (batch/sub-batch child nodes) for one stage. */
    stage: (runId: string, stage: string) =>
      [...queryKeys.workflowRuns.all, runId, 'stage', stage] as const,
    /** Full nested execution tree. */
    tree: (runId: string) => [...queryKeys.workflowRuns.all, runId, 'tree'] as const,
    /** Per-product ledger, scoped per filter permutation (stage/status/batch).
     *  Cursor is NOT part of the key — infinite-query pages accumulate under it. */
    products: (runId: string, filters: Record<string, unknown>) =>
      [...queryKeys.workflowRuns.all, runId, 'products', filters] as const,
    /** One product's journey + provenance. */
    productJourney: (runId: string, productId: string) =>
      [...queryKeys.workflowRuns.all, runId, 'products', productId] as const,
    /** Data-lineage graph (assets + links). */
    lineage: (runId: string) => [...queryKeys.workflowRuns.all, runId, 'lineage'] as const,
    /** Control + lifecycle audit timeline. */
    controlHistory: (runId: string) =>
      [...queryKeys.workflowRuns.all, runId, 'control-history'] as const,
    /** Shadow-parity report (ops/QA). */
    parity: (runId: string) => [...queryKeys.workflowRuns.all, runId, 'parity'] as const,
  },
  /**
   * AI Performance Insights — one `workflowId`-scoped subtree per run, so a panel
   * recompute can invalidate every screen for that run in a single call. Panels are
   * precomputed and change only when the loader reruns, so nothing here is polled.
   */
  aiPerformance: {
    all: ['ai-performance'] as const,
    /** Screens 1/2/4 + the freshness banner — one payload. */
    summary: (workflowId: string) =>
      [...queryKeys.aiPerformance.all, workflowId, 'summary'] as const,
    /** Screen 3 — demand-gap terms bucketed by quadrant. */
    demandGap: (workflowId: string) =>
      [...queryKeys.aiPerformance.all, workflowId, 'demand-gap'] as const,
    /** Screen 5 — live per-product drill-down. `connectorCode` is part of the key
     *  because a different connector is a different backend read (and, outside GMC,
     *  a 400), not a variant of the same payload. */
    product: (workflowId: string, productId: string, connectorCode?: string) =>
      [
        ...queryKeys.aiPerformance.all,
        workflowId,
        'product',
        productId,
        connectorCode ?? null,
      ] as const,
  },
  /**
   * Content service — one `content` root so a run mutation (pause/resume/cancel)
   * can invalidate every read for that run in a single call. Run-scoped reads are
   * keyed under the runId; the filter object is folded into the list key so each
   * status permutation caches independently.
   */
  content: {
    all: ['content'] as const,
    /** Readiness probe, for the degraded-service banner. */
    health: () => [...queryKeys.content.all, 'health'] as const,
    /** Static flow vocabulary — effectively immutable, fetched once. */
    flows: () => [...queryKeys.content.all, 'flows'] as const,
    /** Selectable prompt versions, grouped by flow, plus one version's body. */
    prompts: {
      all: () => [...queryKeys.content.all, 'prompts'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.content.prompts.all(), 'list', params] as const,
      detail: (flow: string, version: string, params: Record<string, unknown>) =>
        [...queryKeys.content.prompts.all(), 'detail', flow, version, params] as const,
    },
    /**
     * Attribution. The product-scoped reads carry no runId — attributes are
     * computed once per product and reused, so they are tenant-scoped facts
     * rather than run output. The run-scoped pair lives under `runs.byId`.
     */
    attribution: {
      all: () => [...queryKeys.content.all, 'attribution'] as const,
      product: (productId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.attribution.all(), 'product', productId, params] as const,
      productTypes: (params: Record<string, unknown>) =>
        [...queryKeys.content.attribution.all(), 'product-types', params] as const,
      attributes: (params: Record<string, unknown>) =>
        [...queryKeys.content.attribution.all(), 'attributes', params] as const,
      /** Every grounding roll-up, whatever its scope — the target after a taxonomy job. */
      groundingAll: () => [...queryKeys.content.attribution.all(), 'grounding'] as const,
      grounding: (params: Record<string, unknown>) =>
        [...queryKeys.content.attribution.groundingAll(), params] as const,
      /** Every product view of one product, whatever its params — the optimistic-update target. */
      productAll: (productId: string) =>
        [...queryKeys.content.attribution.all(), 'product', productId] as const,
      /** One product's full review history (newest first). */
      productReviews: (productId: string) =>
        [...queryKeys.content.attribution.all(), 'product-reviews', productId] as const,
    },
    /** Taxonomy maintenance jobs — OUTSIDE `attribution`, so invalidating the attribution reads
     *  when a job closes never refetches the job itself. */
    /** One channel's enrichment field registry (`GET /enrichments/fields`), keyed by connector. */
    enrichmentFields: (connector: string) =>
      [...queryKeys.content.all, 'enrichment-fields', connector] as const,
    taxonomy: {
      all: () => [...queryKeys.content.all, 'taxonomy'] as const,
      vectorReconcile: () => [...queryKeys.content.taxonomy.all(), 'vector-reconcile'] as const,
      attributeReground: () => [...queryKeys.content.taxonomy.all(), 'attribute-reground'] as const,
      /** Every history read (paged list and the 30-day walk) — the target when a job starts or closes. */
      historyAll: () => [...queryKeys.content.taxonomy.all(), 'history'] as const,
      history: (params: Record<string, unknown>) =>
        [...queryKeys.content.taxonomy.historyAll(), params] as const,
      /** History walked back over the last `days` days, for the counts and the dot strips. */
      historyWindow: (days: number) =>
        [...queryKeys.content.taxonomy.historyAll(), 'window', days] as const,
    },
    /** Model configuration — catalog is platform-wide, versions are per scope. */
    modelConfiguration: {
      all: () => [...queryKeys.content.all, 'model-configuration'] as const,
      catalog: (includeDeprecated: boolean) =>
        [...queryKeys.content.modelConfiguration.all(), 'catalog', includeDeprecated] as const,
      catalogCurrent: () =>
        [...queryKeys.content.modelConfiguration.all(), 'catalog-current'] as const,
      configuration: (scope: string) =>
        [...queryKeys.content.modelConfiguration.all(), 'configuration', scope] as const,
      resolved: () => [...queryKeys.content.modelConfiguration.all(), 'resolved'] as const,
      version: (scope: string, configVersion: number) =>
        [...queryKeys.content.modelConfiguration.all(), 'version', scope, configVersion] as const,
    },
    runs: {
      all: () => [...queryKeys.content.all, 'runs'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.content.runs.all(), 'list', params] as const,
      /** Every read scoped to one run — the invalidation target after a control action. */
      byId: (runId: string) => [...queryKeys.content.runs.all(), runId] as const,
      detail: (runId: string) => [...queryKeys.content.runs.byId(runId), 'detail'] as const,
      batches: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'batches', params] as const,
      metrics: (runId: string) => [...queryKeys.content.runs.byId(runId), 'metrics'] as const,
      generated: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'content', params] as const,
      failures: (runId: string) => [...queryKeys.content.runs.byId(runId), 'failures'] as const,
      /** Per-flow progress — polled while the run is in flight. */
      stages: (runId: string) => [...queryKeys.content.runs.byId(runId), 'stages'] as const,
      /** One batch's stages and sub-batches. */
      batch: (runId: string, batchIndex: number) =>
        [...queryKeys.content.runs.byId(runId), 'batch', batchIndex] as const,
      /** One batch's products, per page/size/platform. */
      batchProducts: (runId: string, batchIndex: number, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'batch', batchIndex, 'products', params] as const,
      /** What the run did to each product, per filter set (keyset pages accumulate). */
      products: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'products', params] as const,
      /** One product's run row, found by paging the products list (no product filter exists). */
      productLookup: (runId: string, productId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'product-lookup', productId, params] as const,
      /** The products list's (vertical, status) counts, per filter set. */
      productCounts: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'product-counts', params] as const,
      /** Only what this run WROTE — reused detections are not in here. */
      productTypes: (runId: string) =>
        [...queryKeys.content.runs.byId(runId), 'product-types'] as const,
      attributes: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'attributes', params] as const,
      /** QC findings this run's rule checks wrote. */
      qcFindings: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'qc-findings', params] as const,
      /** QC report for the run: findings per rule and severity. */
      qcSummary: (runId: string) => [...queryKeys.content.runs.byId(runId), 'qc-summary'] as const,
      /** Review list pages (one entry per filter set) and the header stats. */
      reviewProducts: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'review-products', params] as const,
      reviewStats: (runId: string) =>
        [...queryKeys.content.runs.byId(runId), 'review-stats'] as const,
      /** The review export CSV, parsed into decision counts (verdict × subject, checks, reviewers). */
      reviewDecisions: (runId: string) =>
        [...queryKeys.content.runs.byId(runId), 'review-decisions'] as const,
      /** One source run's decisions on a reused check's products (under the source's key, so a save there refreshes it). */
      reviewDecisionsFor: (sourceRunId: string, forRunId: string) =>
        [...queryKeys.content.runs.reviewDecisions(sourceRunId), forRunId] as const,
      /** Gap analyses are per run, and their findings per analysis. */
      gapAnalyses: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'gap-analyses', params] as const,
      gapReport: (runId: string, analysisId: string) =>
        [...queryKeys.content.runs.byId(runId), 'gap-analyses', analysisId, 'report'] as const,
      gapFindings: (runId: string, analysisId: string, params: Record<string, unknown>) =>
        [
          ...queryKeys.content.runs.byId(runId),
          'gap-analyses',
          analysisId,
          'findings',
          params,
        ] as const,
      /** Workflow admin reads, all scoped to the run so a reconcile clears them together. */
      workflow: (runId: string) => [...queryKeys.content.runs.byId(runId), 'workflow'] as const,
      workflowHistory: (runId: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'workflow-history', params] as const,
      drift: (runId: string) => [...queryKeys.content.runs.byId(runId), 'drift'] as const,
      /** Quality scores for one run — `view` is summary/matrix/checks/verticals/products/product. */
      scores: (runId: string, view: string, params: Record<string, unknown>) =>
        [...queryKeys.content.runs.byId(runId), 'scores', view, params] as const,
    },
    /** Quality trends across a tenant's runs (not run-scoped). */
    scoreTrends: (params: Record<string, unknown>) =>
      [...queryKeys.content.all, 'score-trends', params] as const,
    /** The Overview dashboard's cross-run reads, one key per read and its params (period included). */
    overview: {
      all: () => [...queryKeys.content.all, 'overview'] as const,
      dashboard: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'dashboard', params] as const,
      attributionSummary: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'attribution-summary', params] as const,
      attributionTrends: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'attribution-trends', params] as const,
      products: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'products', params] as const,
      missing: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'missing', params] as const,
      qcSummary: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'qc-summary', params] as const,
      qcFindings: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'qc-findings', params] as const,
      reviewsSummary: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'reviews-summary', params] as const,
      reviewQueue: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'review-queue', params] as const,
      runCount: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'run-count', params] as const,
      recentRuns: (params: Record<string, unknown>) =>
        [...queryKeys.content.overview.all(), 'recent-runs', params] as const,
    },
  },
  /**
   * Read-only Temporal inspection (`/workflows/{workflowId}/…`). One subtree per
   * workflow execution (`runId` = Temporal run, `null` = current), so a run's
   * whole inspector can be invalidated at once when it closes.
   */
  workflowInspection: {
    all: ['workflow-inspection'] as const,
    execution: (workflowId: string, runId: string | null) =>
      [...queryKeys.workflowInspection.all, workflowId, runId] as const,
    history: (workflowId: string, runId: string | null) =>
      [...queryKeys.workflowInspection.execution(workflowId, runId), 'history'] as const,
    describe: (workflowId: string, runId: string | null) =>
      [...queryKeys.workflowInspection.execution(workflowId, runId), 'describe'] as const,
    children: (workflowId: string, runId: string | null, includeLiveStatus: boolean) =>
      [
        ...queryKeys.workflowInspection.execution(workflowId, runId),
        'children',
        includeLiveStatus,
      ] as const,
    workers: (workflowId: string, runId: string | null) =>
      [...queryKeys.workflowInspection.execution(workflowId, runId), 'workers'] as const,
    stackTrace: (workflowId: string, runId: string | null) =>
      [...queryKeys.workflowInspection.execution(workflowId, runId), 'stack-trace'] as const,
    queries: (workflowId: string, runId: string | null) =>
      [...queryKeys.workflowInspection.execution(workflowId, runId), 'queries'] as const,
  },
  // ----- catalog-v2 -----
  /**
   * Catalog service (`/catalog-v2/*` screens). Keys nest under one run / product /
   * schedule / experiment id so a control action can invalidate everything about
   * that entity with `run(runRef)` etc. `params` objects hold the query filters.
   */
  catalogService: {
    all: ['catalog-service'] as const,
    /** Global, not run-scoped — the backend's one-line description per stage (B1). */
    stageMetadata: () => [...queryKeys.catalogService.all, 'stage-metadata'] as const,
    runs: {
      all: () => [...queryKeys.catalogService.all, 'runs'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.runs.all(), 'list', params] as const,
      statusCounts: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.runs.all(), 'status-counts', params] as const,
      /** The "Created by" filter's options (GET catalog-runs/creators) under the list filters. */
      creators: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.runs.all(), 'creators', params] as const,
      summary: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.runs.all(), 'summary', params] as const,
      failures: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.runs.all(), 'failures', params] as const,
    },
    /** Everything scoped to one run — invalidate after any control action on it. */
    run: (runRef: string) => [...queryKeys.catalogService.all, 'run', runRef] as const,
    runDetail: (runRef: string) => [...queryKeys.catalogService.run(runRef), 'detail'] as const,
    runProgress: (runRef: string) => [...queryKeys.catalogService.run(runRef), 'progress'] as const,
    /** One run's `GET catalog-runs/summaries` row, cached per id (fetched batched, §89). */
    runSummary: (runRef: string) => [...queryKeys.catalogService.run(runRef), 'summary'] as const,
    runStageAttempts: (runRef: string, stage: string) =>
      [...queryKeys.catalogService.run(runRef), 'attempts', stage] as const,
    runFieldSets: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'field-sets'] as const,
    runFailurePoints: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'failure-points'] as const,
    /** §93 — `GET …/errors`, the run's stage-error ledger. */
    runStageErrors: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'stage-errors'] as const,
    /** §95 — `GET …/cost-estimate`, the run's latest cost gate estimate. */
    runCostEstimate: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'cost-estimate'] as const,
    /** §95 — subsidiary-wide cost gate reads (pending list, approver status). */
    costEstimates: {
      all: () => [...queryKeys.catalogService.all, 'cost-estimates'] as const,
      pending: () => [...queryKeys.catalogService.costEstimates.all(), 'pending'] as const,
      history: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.costEstimates.all(), 'history', params] as const,
      /** Per signed-in user, like `costEstimates.approverStatus`; no `userId` = the prefix. */
      approverStatus: (userId?: string) =>
        userId === undefined
          ? ([...queryKeys.catalogService.costEstimates.all(), 'approver-status'] as const)
          : ([...queryKeys.catalogService.costEstimates.all(), 'approver-status', userId] as const),
    },
    /** Finding 13 — `GET …/delivery/rejections` (B1, keyset-paged). */
    runDeliveryRejections: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'delivery-rejections'] as const,
    /** #160 — `GET …/actions` (S9, keyset-paged, newest first). */
    runControlActions: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'control-actions', params] as const,
    /** #160 — `GET …/nodes` (S9, whole tree in one read). */
    runNodes: (runRef: string) => [...queryKeys.catalogService.run(runRef), 'nodes'] as const,
    /** #160 — child runs of this one (`GET catalog-runs?parentRunId=`), for "Re-runs of this batch". */
    runChildRuns: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'child-runs', params] as const,
    runLineage: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'lineage', params] as const,
    runLineageSummary: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'lineage-summary'] as const,
    /** `GET …/lineage/listings` — one row per listing (api-changes §49). */
    runLineageListings: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'lineage-listings', params] as const,
    /** `GET …/lineage/issues` — issue groups (api-changes §49). */
    runLineageIssues: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'lineage-issues', params] as const,
    /** `GET …/lineage/issues/listings` — one issue group's listings. */
    runLineageIssueListings: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'lineage-issue-listings', params] as const,
    curationStats: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'curation-stats'] as const,
    curationOutcomes: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'curation-outcomes'] as const,
    curationProducts: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'curation-products', params] as const,
    curationSkipped: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'curation-skipped', params] as const,
    /** Content-generation reads composed into a run screen (contentClient). */
    runContent: (runRef: string, kind: string, params: Record<string, unknown> = {}) =>
      [...queryKeys.catalogService.run(runRef), 'content', kind, params] as const,
    /**
     * Summary funnel (docs/run-summary-funnel.md §7.1) — one key per section so each loads and
     * fails independently (§10 step 5 rule 3). `funnelSummary` polls every 5s while the run is
     * active; the rest are lazy-on-open and cached.
     */
    funnelSummary: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-summary'] as const,
    funnelStageReasons: (runRef: string, stage: string, attempt: number | null) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-stage-reasons', stage, attempt] as const,
    funnelPublish: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-publish'] as const,
    funnelVerify: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-verify'] as const,
    funnelComparison: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-comparison'] as const,
    /** ⑦ lazy, stage-page scoped — one key per (stage, attempt) so switching stages/attempts
     *  never shows a stale previous stage's detail while the new one loads. */
    funnelStageDetail: (runRef: string, stage: string, attempt: number | null) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-stage-detail', stage, attempt] as const,
    /** ⑧ lazy, stage page's Timeline tab — `stage: null` is the whole-run timeline. */
    funnelTimeline: (runRef: string, stage: string | null, limit: number) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-timeline', stage, limit] as const,
    /** ⑨ the mix's Products matrix — one key per filter set so switching the selected stage or a
     *  filter never shows a stale previous page while the new one loads. */
    funnelProducts: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-products', params] as const,
    /** The products behind one Insights scorecard row (FIELD/CHECK) — one key per
     *  (kind, key, category, group, page, size) so a group or page switch never shows stale rows. */
    scorecardProducts: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'scorecard-products', params] as const,
    /** R13 — a catalog run's pre+post score reads (`summary` | `checks` | `products`), keyed by
     *  the catalog `runRef` (no content-gen run-id stitching) + the read's params. */
    catalogRunScores: (runRef: string, kind: string, params: Record<string, unknown> = {}) =>
      [...queryKeys.catalogService.run(runRef), 'catalog-run-scores', kind, params] as const,
    /** R10 — the run's content metrics against the previous batch on its connector. */
    catalogRunMetricsCompare: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'metrics-compare'] as const,
    /** R11 — one product's tag / trend / occasion chips before enrichment. */
    productChipsBefore: (runRef: string, productId: string) =>
      [...queryKeys.catalogService.run(runRef), 'product-chips-before', productId] as const,
    /** One page of a signal row's membership (Insights detail view), keyed like the above. */
    signalMembershipPage: (runRef: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.run(runRef), 'signal-membership-page', params] as const,
    /** The stage pane's "3 worst cases" thumbnails — one small composite read per stage. */
    funnelWorstCases: (runRef: string, stage: string, sourceId: string | null) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-worst-cases', stage, sourceId] as const,
    /** CG's live progress for this catalog run (§7 ⑦) — polls only while a content stage runs
     *  AND its detail panel is open. */
    funnelContentProgress: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'funnel-content-progress'] as const,
    /** CG reads keyed by content-gen's OWN run id (one `generationRunId`), not the catalog runRef —
     *  distinct root so they never collide with `run()`-scoped catalog keys. */
    funnelContentRun: (generationRunId: string) =>
      [...queryKeys.catalogService.all, 'funnel-content-run', generationRunId] as const,
    funnelContentMetrics: (generationRunId: string) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'metrics'] as const,
    funnelContentProductCounts: (generationRunId: string, params: Record<string, unknown>) =>
      [
        ...queryKeys.catalogService.funnelContentRun(generationRunId),
        'product-counts',
        params,
      ] as const,
    funnelContentProducts: (generationRunId: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'products', params] as const,
    /** I14 — one keyset page of a content-gen run's item failures (cursor + product filter). */
    funnelContentFailures: (generationRunId: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'failures', params] as const,
    /** I14 — one content-gen run's review header (severity totals, products with findings). */
    funnelContentReviewStats: (generationRunId: string) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'review-stats'] as const,
    /** I14 — one content-gen run's QC report (per check × severity). */
    funnelContentQcSummary: (generationRunId: string) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'qc-summary'] as const,
    /** R9 — one content-gen run's run-level + failure events (the stage timeline's merge). */
    funnelContentEvents: (generationRunId: string) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'events'] as const,
    /** R16 — one content-gen run's QC findings grouped by message pattern. */
    funnelContentQcPatterns: (generationRunId: string) =>
      [...queryKeys.catalogService.funnelContentRun(generationRunId), 'qc-patterns'] as const,
    /** I14 — one page of a content-gen run's QC findings. */
    funnelContentQcFindings: (generationRunId: string, params: Record<string, unknown>) =>
      [
        ...queryKeys.catalogService.funnelContentRun(generationRunId),
        'qc-findings',
        params,
      ] as const,
    /** I7 — operator edits recorded against one curation feed product (content-gen ledger). */
    feedProductAttributeEdits: (feedProductId: string, connectorCode: string | null) =>
      [
        ...queryKeys.catalogService.all,
        'feed-product',
        feedProductId,
        'attribute-edits',
        connectorCode,
      ] as const,
    /** One content-gen run's `scores/*` read (summary, matrix, checks, products…) — keyed by the
     *  run id + platform + params, so the PRE and POST runs of a batch cache apart. */
    contentRunScores: (generationRunId: string, kind: string, params: Record<string, unknown>) =>
      [
        ...queryKeys.catalogService.funnelContentRun(generationRunId),
        'scores',
        kind,
        params,
      ] as const,
    /** One content-gen run's gap analyses (list, or one analysis's report). */
    contentRunGapAnalyses: (generationRunId: string, analysisId: string | null) =>
      [
        ...queryKeys.catalogService.funnelContentRun(generationRunId),
        'gap-analyses',
        analysisId,
      ] as const,
    /** Whether the run's Performance Signals cards are recomputing (content-gen status read). */
    performanceInsightsStatus: (runRef: string) =>
      [...queryKeys.catalogService.run(runRef), 'performance-insights-status'] as const,
    triggerOptions: (connectorCode: string) =>
      [...queryKeys.catalogService.all, 'trigger-options', connectorCode] as const,
    triggerFilters: (connectorCode: string) =>
      [...queryKeys.catalogService.all, 'trigger-filters', connectorCode] as const,
    /** #205 (S11, B1) — New batch's per-stage estimate for a flow+connector pair. */
    runEstimate: (flow: string, connectorCode: string) =>
      [...queryKeys.catalogService.all, 'estimate', flow, connectorCode] as const,
    /** #205 (S11, B3) — New batch's live per-filter + whole-scope match counts. */
    triggerFilterCounts: (params: Record<string, unknown>) =>
      [...queryKeys.catalogService.all, 'trigger-filter-counts', params] as const,
    /** #205 (S11, B3) — New batch's top values for one filter key. */
    triggerFilterValues: (params: Record<string, unknown>) =>
      [...queryKeys.catalogService.all, 'trigger-filter-values', params] as const,
    /** #205 (S11, B4) — syncs a New batch could reuse. */
    connectorSyncs: (connectorCode: string) =>
      [...queryKeys.catalogService.all, 'connector-syncs', connectorCode] as const,
    curationFilters: (connectorCode: string | null) =>
      [...queryKeys.catalogService.all, 'curation-filters', connectorCode] as const,
    schedules: {
      all: () => [...queryKeys.catalogService.all, 'schedules'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.schedules.all(), 'list', params] as const,
      ribbon: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.schedules.all(), 'ribbon', params] as const,
      overview: () => [...queryKeys.catalogService.schedules.all(), 'overview'] as const,
      feed: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.schedules.all(), 'feed', params] as const,
      detail: (scheduleId: string) =>
        [...queryKeys.catalogService.schedules.all(), 'detail', scheduleId] as const,
      firings: (scheduleId: string, params: Record<string, unknown>) =>
        [...queryKeys.catalogService.schedules.all(), 'firings', scheduleId, params] as const,
      tree: () => [...queryKeys.catalogService.schedules.all(), 'tree'] as const,
      scheduleTree: (scheduleId: string) =>
        [...queryKeys.catalogService.schedules.all(), 'tree', scheduleId] as const,
    },
    needsAttention: {
      all: () => [...queryKeys.catalogService.all, 'needs-attention'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.needsAttention.all(), 'list', params] as const,
      labels: () => [...queryKeys.catalogService.needsAttention.all(), 'labels'] as const,
      stats: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.needsAttention.all(), 'stats', params] as const,
      /** `GET needs-attention/counts` — whole-queue reason counts. */
      counts: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.needsAttention.all(), 'counts', params] as const,
    },
    feedProduct: (feedProductId: string) =>
      [...queryKeys.catalogService.all, 'feed-product', feedProductId] as const,
    feedProductImages: (feedProductId: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.feedProduct(feedProductId), 'images', params] as const,
    feedProductVersions: (feedProductId: string) =>
      [...queryKeys.catalogService.feedProduct(feedProductId), 'versions'] as const,
    feedProductSource: (feedProductId: string) =>
      [...queryKeys.catalogService.feedProduct(feedProductId), 'source'] as const,
    products: {
      all: () => [...queryKeys.catalogService.all, 'products'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.products.all(), 'list', params] as const,
    },
    /** Everything scoped to one catalog product (detail, lineage, images; the set rides on detail). */
    product: (productId: string) =>
      [...queryKeys.catalogService.all, 'product', productId] as const,
    productDetail: (productId: string) =>
      [...queryKeys.catalogService.product(productId), 'detail'] as const,
    productLineage: (productId: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.product(productId), 'lineage', params] as const,
    productImages: (productId: string) =>
      [...queryKeys.catalogService.product(productId), 'images'] as const,
    /** `GET catalog-products/{id}/variants` — one server-side page (api-changes §48). */
    productVariants: (productId: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.product(productId), 'variants', params] as const,
    images: {
      all: () => [...queryKeys.catalogService.all, 'images'] as const,
      health: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.images.all(), 'health', params] as const,
      failures: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.images.all(), 'failures', params] as const,
      assetUsages: (contentHash: string, params: Record<string, unknown>) =>
        [...queryKeys.catalogService.images.all(), 'asset-usages', contentHash, params] as const,
      /** `POST images/usages/query` — many hashes' usages in one read. */
      assetUsagesQuery: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.images.all(), 'asset-usages-query', params] as const,
      /** `POST feed-products/images/query` — many listings' images in one read. */
    },
    /** catalog-v2 experiments — distinct from legacy `queryKeys.experiments` and `analytics.experiments`. */
    experiments: {
      all: () => [...queryKeys.catalogService.all, 'experiments'] as const,
      list: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.experiments.all(), 'list', params] as const,
      /** `GET experiments/status-counts` — every chip count in one read (api-changes §49). */
      statusCounts: (params: Record<string, unknown>) =>
        [...queryKeys.catalogService.experiments.all(), 'status-counts', params] as const,
    },
    /** Everything scoped to one experiment — invalidate after any experiment control. */
    experiment: (experimentId: string) =>
      [...queryKeys.catalogService.all, 'experiment', experimentId] as const,
    experimentDetail: (experimentId: string) =>
      [...queryKeys.catalogService.experiment(experimentId), 'detail'] as const,
    experimentProgress: (experimentId: string) =>
      [...queryKeys.catalogService.experiment(experimentId), 'progress'] as const,
    experimentPipelinePreview: (experimentId: string) =>
      [...queryKeys.catalogService.experiment(experimentId), 'pipeline-preview'] as const,
    experimentSampleAttempts: (experimentId: string) =>
      [...queryKeys.catalogService.experiment(experimentId), 'sample-attempts'] as const,
    experimentArmAssignments: (experimentId: string, params: Record<string, unknown>) =>
      [...queryKeys.catalogService.experiment(experimentId), 'arm-assignments', params] as const,
  },
  // ----- end catalog-v2 -----
  // ----- taxonomy-studio -----
  /**
   * Taxonomy Studio. Every read that depends on a version carries the release ref
   * (`'draft' | 'live' | '<seq>'`) so switching versions never serves another version's rows;
   * writes invalidate `all` (numbered releases are immutable and HTTP-cached, so their refetch
   * is free).
   */
  taxonomyStudio: {
    all: ['taxonomy'] as const,
    permissions: () => [...queryKeys.taxonomyStudio.all, 'permissions'] as const,
    draft: () => [...queryKeys.taxonomyStudio.all, 'draft'] as const,
    checks: () => [...queryKeys.taxonomyStudio.all, 'draft-checks'] as const,
    actions: () => [...queryKeys.taxonomyStudio.all, 'draft-actions'] as const,
    releases: () => [...queryKeys.taxonomyStudio.all, 'releases'] as const,
    release: (seq: string) => [...queryKeys.taxonomyStudio.all, 'release', seq] as const,
    releaseChanges: (seq: string, params: Record<string, unknown>) =>
      [...queryKeys.taxonomyStudio.all, 'release-changes', seq, params] as const,
    restorePreview: (seq: number) =>
      [...queryKeys.taxonomyStudio.all, 'restore-preview', seq] as const,
    diff: (params: Record<string, unknown>) =>
      [...queryKeys.taxonomyStudio.all, 'diff', params] as const,
    changes: (params: Record<string, unknown>) =>
      [...queryKeys.taxonomyStudio.all, 'changes', params] as const,
    nodeChildren: (parentId: number | null, release: string, status: string) =>
      [...queryKeys.taxonomyStudio.all, 'node-children', parentId, release, status] as const,
    nodeSearch: (q: string, release: string, status: string) =>
      [...queryKeys.taxonomyStudio.all, 'node-search', q, release, status] as const,
    node: (id: number, release: string) =>
      [...queryKeys.taxonomyStudio.all, 'node', id, release] as const,
    nodeResolved: (id: number, release: string) =>
      [...queryKeys.taxonomyStudio.all, 'node-resolved', id, release] as const,
    archivePreview: (id: number) =>
      [...queryKeys.taxonomyStudio.all, 'archive-preview', id] as const,
    sections: (params: Record<string, unknown>) =>
      [...queryKeys.taxonomyStudio.all, 'sections', params] as const,
    section: (key: string, release: string) =>
      [...queryKeys.taxonomyStudio.all, 'section', key, release] as const,
    sectionValues: (key: string, release: string) =>
      [...queryKeys.taxonomyStudio.all, 'section-values', key, release] as const,
    sectionAttachments: (key: string, release: string) =>
      [...queryKeys.taxonomyStudio.all, 'section-attachments', key, release] as const,
    uploads: (params: Record<string, unknown>) =>
      [...queryKeys.taxonomyStudio.all, 'uploads', params] as const,
    upload: (id: string) => [...queryKeys.taxonomyStudio.all, 'upload', id] as const,
    uploadRows: (id: string, params: Record<string, unknown>) =>
      [...queryKeys.taxonomyStudio.all, 'upload-rows', id, params] as const,
  },
  // ----- end taxonomy-studio -----
} as const;
